/**
 * AuthenticatedApp — cuerpo real de la aplicación tras autenticación.
 *
 * Feb-2026: extraído de App.js (era una función de ~5670 LOC) para desatascar
 * el resto de refactors sin romper nada. Todos los hooks (useState, useEffect,
 * useCallback, useMemo) viven aquí para garantizar consistencia de hook-count
 * entre renders. NO reordenar ni añadir hooks condicionales.
 *
 * MainApp (auth gate) sigue en App.js y monta este componente cuando el
 * usuario está autenticado.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { Plus, Edit2, Trash2, Music, Heart, X, Download, LogOut, FileText, ExternalLink, Link as LinkIcon, Sparkles, Check, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../AuthContext";
import { useSongsContext } from "../contexts/SongsContext";
import { usePlaylistsContext } from "../contexts/PlaylistsContext";
import { Login } from "../Login";

// O-02: lazy — Dashboard carga `recharts` (gráficas), no debe ir en el bundle
// inicial eager de AuthenticatedApp; se descarga solo al entrar a esta vista.
const Dashboard = React.lazy(() => import("../Dashboard").then((m) => ({ default: m.Dashboard })));

import { SpotifySearchDialog } from "../components/SpotifySearchDialog";
import { SongLibrary } from "../components/SongLibrary";
import { PlaylistAnalyzer } from "../components/PlaylistAnalyzer";
import { EventsList } from "../components/EventsList";
import { OfflineIndicator, InstallPrompt } from "../components/OfflineIndicator";

import { SyncStatusBadge } from "../components/SyncStatusBadge";
import { AppTabs } from "../components/AppTabs";
import { AdminNavButtons } from "../components/AdminNavButtons";
import { AddSongToPlaylistDialog } from "../components/AddSongToPlaylistDialog";

import { AddSongDialog } from "../components/AddSongDialog";
import { EditDialog } from "../components/EditDialog";
import { NotesDialog } from "../components/NotesDialog";
import { AddStyleDialog } from "../components/AddStyleDialog";
import { DeleteStyleDialog } from "../components/DeleteStyleDialog";

// Imports movidos a /components/SortablePeticionItem.jsx

import { TodayDJView } from "../components/TodayDJView";
import { TodayBadge } from "../components/TodayBadge";

import useKeyboardShortcuts from "../hooks/useKeyboardShortcuts";
import { KeyboardShortcutsHelp } from "../components/KeyboardShortcutsHelp";

import { useOfflineData } from "../hooks/useOfflineData";

import offlineDB from "../utils/offlineDB";
import { runAiJob } from "../utils/aiJobs";
import { getAudioFromCache } from "../utils/audioCache";

import { SpotifyProvider, useSpotify } from "../SpotifyContext";
import { PlaylistsProvider } from "../contexts/PlaylistsContext";
import { SongsProvider } from "../contexts/SongsContext";
import { SpotifyCallback } from "../SpotifyCallback";
import { SpotifyControls } from "../components/SpotifyControls";
import { InstallPWA } from "../components/InstallPWA";
import { SpotifyQueue } from "../components/SpotifyQueue";

import { PlaylistForm } from "../PlaylistForm";

// O-02: lazy — CalendarView carga `react-big-calendar` (+ CSS), igual que Dashboard.
const CalendarView = React.lazy(() => import("../CalendarView").then((m) => ({ default: m.CalendarView })));

import { ClientPortal } from "../ClientPortal";

import { TendenciasView } from "../TendenciasView";
import SmartPlaylists from "../components/SmartPlaylists";
import { UpdateNotification } from "../components/AutoUpdate";
import DJAssistant from "../components/DJAssistant";
import { WelcomePage } from "../components/WelcomePage";
import { TaskForm } from "../components/TaskForm";

import { TasksView } from "../TasksView";
import { useTaskReminders } from "../hooks/useTaskReminders";
import { useCacheStats } from "../hooks/useCacheStats";
import { useUrlActionTriggers } from "../hooks/useUrlActionTriggers";
import { useAudioPreload } from "../hooks/useAudioPreload";
import { useUrlEventSync } from "../hooks/useUrlEventSync";
import { usePeticionesHandlers } from "../hooks/usePeticionesHandlers";
import { useSharedNotes } from "../hooks/useSharedNotes";
import { useDuplicatesDetection } from "../hooks/useDuplicatesDetection";

import { LiveGuestPortal2 } from "../LiveGuestPortal2";

import { PublicPresupuestoPage } from "../PublicPresupuestoPage";
import { GlobalSearch } from "../components/GlobalSearch";

import { InformesView } from "../components/InformesView";
import EventWizard from "../EventWizard";
import LiveTrendsView from "../LiveTrendsView";
import MixSuggestionsModal from "../components/MixSuggestionsModal";
import MixChainsManager from "../components/MixChainsManager";
import WhatsAppImporter from "../components/WhatsAppImporter";
import ClientTextImportDialog from "../components/ClientTextImportDialog";

import StarRating from "../components/StarRating";

import DossierPublicView from "../components/DossierPublicView";
import DossierGenerator from "../components/DossierGenerator";
// Páginas extraídas (Phase 2 refactor) — iter81: lazy-loaded para reducir bundle inicial
// iter85: prefetch on hover/touch movido a /utils/lazyPrefetch.js
import { Suspense } from "react";
import logger from '@/utils/logger';
import { LazyPages } from '../utils/lazyPrefetch';
// Layout compartido
import EdgeSwipeBackIndicator from "../components/EdgeSwipeBackIndicator";
import AuthenticatedLayout from "../layouts/AuthenticatedLayout";
import { INITIAL_GENRES, MOMENTOS_BODA, normalizeText } from "../constants";
import { API_URL } from '../config';
import PlaylistDetailView from "../components/PlaylistDetailView";
import { MainAppContext } from "../contexts/MainAppContext";
import { AudioPlaybackContext } from "../contexts/AudioPlaybackContext";
import { PeticionesContext } from "../contexts/PeticionesContext";
import { CustomListContext } from "../contexts/CustomListContext";
import { NotesContext } from "../contexts/NotesContext";
import ErrorBoundary from "../components/ErrorBoundary";

// Destructuring de páginas lazy — colocado tras todos los imports para que analizadores
// estáticos (grep/AST) puedan identificar correctamente `LazyPages` como usado.
const {
  ReportsPage,
  CalendarPage,
  TasksPage,
  TrendsPage,
  TeamPage,
  StaffTimingPage,
  FinancesPage,
  PresupuestosPage,
  UsersPage,
  SettingsPage,
  PreciosPage,
  MP3LibraryPage,
  StoragePage,
  VenuesPage,
  PlantillasPage,
  DashboardPage,
  AuditLogPage,
  WizardHealthPage,
  // LibraryPage queda disponible para uso futuro (no se usa actualmente)
} = LazyPages;


const API = API_URL;

// ============================================================================
// AuthenticatedApp — cuerpo real de la app (solo se monta tras autenticación).
// Todos los hooks (useState, useEffect, useCallback, useMemo) viven aquí
// para garantizar consistencia de hook-count entre renders.
// ============================================================================
function AuthenticatedApp() {
  const { user, loading, logout, isAuthenticated, token } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // Escuchar cambios en localStorage para detectar conexión de Spotify desde otra ventana/pestaña
  React.useEffect(() => {
    const handleStorageChange = (e) => {
      // Detectar cuando se guarda el token de Spotify desde el callback en otra ventana
      if (e.key === 'spotify_auth_timestamp' || e.key === 'spotify_connected') {
        const connected = localStorage.getItem('spotify_connected');
        const accessToken = localStorage.getItem('spotify_access_token');
        
        if (connected === 'true' && accessToken) {
          toast.success('✅ Conectado con Spotify');
          // Recargar para actualizar el estado de Spotify
          window.location.reload();
        }
      }
      
      // Detectar error de Spotify
      if (e.key === 'spotify_auth_error') {
        const error = localStorage.getItem('spotify_auth_error');
        if (error) {
          toast.error(`Error de Spotify: ${error}`);
          localStorage.removeItem('spotify_auth_error');
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // También verificar periódicamente por si el storage event no se dispara (mismo origen)
    const checkSpotifyConnection = setInterval(() => {
      const timestamp = localStorage.getItem('spotify_auth_timestamp');
      const lastChecked = sessionStorage.getItem('last_spotify_check');
      
      if (timestamp && timestamp !== lastChecked) {
        sessionStorage.setItem('last_spotify_check', timestamp);
        const connected = localStorage.getItem('spotify_connected');
        const accessToken = localStorage.getItem('spotify_access_token');
        
        if (connected === 'true' && accessToken) {
          toast.success('✅ Conectado con Spotify');
          clearInterval(checkSpotifyConnection);
          window.location.reload();
        }
      }
    }, 1000); // Verificar cada segundo
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkSpotifyConnection);
    };
  }, []);

  // Capturar tokens de Spotify de la URL (redirect del callback)
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Nuevos parámetros (redirect directo)
    const spotifyToken = params.get('spotify_token');
    const spotifyRefresh = params.get('spotify_refresh');
    const spotifyExpires = params.get('spotify_expires');
    
    // Parámetros antiguos (compatibilidad)
    const spotifyConnected = params.get('spotify_connected');
    const spotifyAccessToken = params.get('spotify_access_token') || spotifyToken;
    const spotifyRefreshToken = params.get('spotify_refresh_token') || spotifyRefresh;
    const spotifyExpiresAt = params.get('spotify_expires_at') || spotifyExpires;
    const spotifyError = params.get('spotify_error');
    
    if (spotifyError) {
      toast.error(`Error de Spotify: ${spotifyError}`);
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Si hay token en la URL (ya sea nuevo o antiguo formato)
    if (spotifyAccessToken) {
      logger.log('[Spotify] Token found in URL, saving...');
      localStorage.setItem('spotify_access_token', spotifyAccessToken);
      if (spotifyRefreshToken) {
        localStorage.setItem('spotify_refresh_token', spotifyRefreshToken);
      }
      if (spotifyExpiresAt) {
        localStorage.setItem('spotify_expires_at', spotifyExpiresAt);
      }
      localStorage.setItem('spotify_connected', 'true');
      
      toast.success('✅ Conectado con Spotify');
      
      // Limpiar URL y recargar para que SpotifyContext cargue los tokens
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
    }
    
    // Si solo viene spotify_connected=true (sin tokens en URL), verificar localStorage
    if (spotifyConnected === 'true' && !spotifyAccessToken) {
      const storedToken = localStorage.getItem('spotify_access_token');
      if (storedToken) {
        logger.log('[Spotify] Connected flag received, tokens already in localStorage');
        toast.success('✅ Conectado con Spotify');
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
      }
    }
  }, [location.search]);

  // Context hooks - NUEVA ARQUITECTURA
  const {
    songs,
    filteredSongs,
    availableGenres,
    totalSongs,
    searchText,
    setSearchText,
    filterGenre,
    setFilterGenre,
    filterRating,
    setFilterRating,
    filterMomento,
    setFilterMomento,
    filterEnergia,
    setFilterEnergia,
    showFavoritesOnly,
    setShowFavoritesOnly,
    showOnlyDenonPendientes,
    setShowOnlyDenonPendientes,
    hasActiveFilters,
    clearAllFilters,
    refetchSongs,
    forceRefreshSongs,
    createSong,
    updateSong,
    deleteSong,
    isLoading: loadingSongs,
  } = useSongsContext();

  const {
    playlists,
    filteredPlaylists,
    currentPlaylist,
    setCurrentPlaylist,
    searchEventTerm,
    setSearchEventTerm,
    refetchPlaylists,
    selectPlaylist,
  } = usePlaylistsContext();
  
  // Hook para gestión de datos offline
  const {
    isOnline,
    getSongs: getOfflineSongs,
    getPlaylists: getOfflinePlaylists,
    cacheSongs: saveSongs,
    cachePlaylists: savePlaylists,
    loadCacheStats: updateCacheStats,
    syncPendingChanges,
    queueOfflineAction,
    clearCache,
    registerBackgroundSync,
    precacheUpcomingEvents
  } = useOfflineData();
  
  // Hook para Spotify
  const {
    isConnected: spotifyConnected,
    accessToken,
    addMultipleToQueue,
    toggleShuffle,
    setRepeatMode
  } = useSpotify();
  
  const [view, setView] = useState("dashboard"); // dashboard | library | playlists | playlist-detail | users | calendar | precios | plantillas | live-requests
  const [selectedSongs, setSelectedSongs] = useState([]); // IDs de canciones seleccionadas
  const [selectedSongsOrdered, setSelectedSongsOrdered] = useState([]); // Array de canciones con orden
  const [isSpotifySendDialogOpen, setIsSpotifySendDialogOpen] = useState(false);
  const [spotifySendOptions, setSpotifySendOptions] = useState({
    shuffle: false,
    repeat: 'off'
  });
  
  // Refetch songs when switching to library view to ensure data is fresh
  useEffect(() => {
    if (view === 'library' && isAuthenticated) {
      logger.log('[App] View changed to library, force refreshing songs...');
      forceRefreshSongs();
    }
  }, [view, isAuthenticated, forceRefreshSongs]);
  
  // Modo En Vivo state
  const [isLiveMode, setIsLiveMode] = useState(false);
  
  // Global Search state
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  
  // Dossier Generator modal state
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  

  // ============================================
  // TASK MANAGEMENT STATE (Fase 1)
  // ============================================
  const [tasks, setTasks] = useState([]);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // (useUrlEventSync se llama más abajo tras declarar todos los setters usados)
  
  // Redirect legacy view states to new React Router routes
  useEffect(() => {
    const routeMap = {
      'team': '/team',
      'finanzas': '/finances',
      'presupuestos': '/presupuestos',
      'users': '/users',
      'precios': '/precios',
      'plantillas': '/plantillas',
      'reports': '/reports',
      'calendar': '/calendar',
      'tasks': '/tasks',
      'tendencias': '/trends'
    };
    
    if (routeMap[view]) {
      navigate(routeMap[view], { replace: true });
    }
  }, [view, navigate]);
  
  // Función auxiliar para ordenar tareas por fecha (más próximas primero)
  const sortTasksByDate = useCallback((tasksArray) => {
    return [...tasksArray].sort((a, b) => {
      // Si alguna no tiene fecha, ponerla al final
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      
      // Comparar fechas: más próximas primero
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return dateA - dateB;
    });
  }, []);
  
  // Fetch tasks
  const fetchTasks = useCallback(async (attempt = 0) => {
    try {
      // Iter91.11: timeout largo + retry con backoff para cold start del backend.
      // Antes en cold start (5-15s típicos en preview) la fetch podía fallar y
      // dejaba `setTasks([])` sin avisar al usuario.
      const response = await axios.get(`${API}/tasks`, { timeout: 25000 });
      setTasks(sortTasksByDate(response.data));
    } catch (error) {
      console.error(`Error fetching tasks (attempt ${attempt + 1}):`, error);

      // Genuinamente offline: usar array vacío silenciosamente (cache de UI)
      if (!navigator.onLine) {
        logger.log('[Tasks] Offline genuine - empty tasks array');
        setTasks([]);
        return;
      }

      // Iter91.11: red lenta / cold start backend → retry con backoff hasta 3 intentos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response)
          && attempt < 3) {
        const delay = Math.min(1000 * 2 ** attempt, 6000);
        logger.log(`[Tasks] Reintentando en ${delay}ms (cold start backend?)...`);
        setTimeout(() => fetchTasks(attempt + 1), delay);
        return;
      }

      // Tras agotar reintentos: NO clobbear tasks vacíos silenciosamente,
      // mostrar al usuario que hay un problema temporal
      if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('No se pudieron cargar las tareas. Comprueba tu conexión y desliza para refrescar.', { duration: 5000 });
      } else {
        toast.error('Error al cargar tareas');
      }
    } finally {
    }
  }, [sortTasksByDate]);
  
  // Create task
  const createTask = useCallback(async (taskData) => {
    try {
      const response = await axios.post(`${API}/tasks`, taskData);
      setTasks(prev => sortTasksByDate([...prev, response.data]));
      toast.success('Tarea creada exitosamente');
      setIsTaskFormOpen(false);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Error al crear tarea');
      throw error;
    }
  }, [sortTasksByDate]);
  
  // Update task
  const updateTask = useCallback(async (taskId, updates) => {
    try {
      const response = await axios.put(`${API}/tasks/${taskId}`, updates);
      setTasks(prev => sortTasksByDate(prev.map(t => t.id === taskId ? response.data : t)));
      toast.success('Tarea actualizada');
      setIsTaskFormOpen(false);
      setEditingTask(null);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar tarea');
      throw error;
    }
  }, [sortTasksByDate]);
  
  // Complete task
  const completeTask = useCallback(async (taskId) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}/complete`);
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'completed', completed_at: new Date().toISOString() }
          : t
      ));
      toast.success('¡Tarea completada! 🎉');
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Error al completar tarea');
    }
  }, []);
  
  // Delete task
  const deleteTask = useCallback(async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Tarea eliminada');
      setIsTaskFormOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Error al eliminar tarea');
    }
  }, []);

  // Delete task series (for recurring tasks)
  const deleteTaskSeries = useCallback(async (taskId, mode = 'only_this') => {
    try {
      const response = await axios.delete(`${API}/tasks/${taskId}/series?mode=${mode}`);
      
      // Recargar todas las tareas para reflejar los cambios
      await fetchTasks();
      
      toast.success(`${response.data.deleted_count} tarea(s) eliminada(s)`);
      setIsTaskFormOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error deleting task series:', error);
      toast.error('Error al eliminar serie de tareas');
    }
  }, [fetchTasks]);

  // Estado para modo de edición de series
  const [seriesEditMode, setSeriesEditMode] = useState(null);

  // Edit task series (opens form with mode context)
  const editTaskSeries = useCallback((task, mode) => {
    setSeriesEditMode(mode);
    setEditingTask(task);
    setIsTaskFormOpen(true);
  }, []);

  // Update task series
  const updateTaskSeries = useCallback(async (taskId, updates, mode = 'only_this') => {
    try {
      const response = await axios.put(`${API}/tasks/${taskId}/series?mode=${mode}`, updates);
      
      // Recargar todas las tareas para reflejar los cambios
      await fetchTasks();
      
      toast.success(`${response.data.updated_count} tarea(s) actualizada(s)`);
      setIsTaskFormOpen(false);
      setEditingTask(null);
      setSeriesEditMode(null);
      return response.data;
    } catch (error) {
      console.error('Error updating task series:', error);
      toast.error('Error al actualizar serie de tareas');
      throw error;
    }
  }, [fetchTasks]);
  
  // Cargar tareas al iniciar
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTasks();
    }
  }, [isAuthenticated, user, fetchTasks]);
  
  // Sistema de recordatorios
  const { requestNotificationPermission } = useTaskReminders(tasks, isAuthenticated);

  // Disparadores URL de modales (?openTaskForm=true, ?openDossier=true)
  useUrlActionTriggers({
    isAuthenticated,
    location,
    navigate,
    setIsTaskFormOpen,
    setIsDossierOpen,
  });
  
  // Solicitar permisos de notificación al iniciar
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      // Esperar 3 segundos después de login para no ser intrusivo
      const timer = setTimeout(() => {
        requestNotificationPermission();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, requestNotificationPermission]);

  // ============================================
  // KEYBOARD SHORTCUTS & NEW FEATURES
  // ============================================
  const [showGenreAnalysis, setShowGenreAnalysis] = useState(false);
  
  const { isHelpOpen, setIsHelpOpen } = useKeyboardShortcuts({
    onNewEvent: () => {
      setView('playlists');
      // Limpiar URL si venía del detalle
      if (location.search.includes('view=playlist-detail')) {
        navigate('/', { replace: true });
      }
      setIsCreatePlaylistOpen(true);
    },
    onNewTask: () => {
      setIsTaskFormOpen(true);
      setEditingTask(null);
    },
    onGlobalSearch: () => {
      setIsGlobalSearchOpen(true);
    },
    onFocusSearch: () => {
      const searchInput = document.querySelector('input[type="search"], input[placeholder*="Buscar"]');
      searchInput?.focus();
    },
    enabled: isAuthenticated
  });

  // AI Recommendations Spotify states removed (Ola cleanup 2026-02): sendAIRecsToSpotify was dead code.
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20; // Mostrar 20 canciones por página para mejor rendimiento
  const [playlistSongs, setPlaylistSongs] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  // Estados para Peticiones
  const [isPeticionesDialogOpen, setIsPeticionesDialogOpen] = useState(false);
  const [isAudioUploaderOpen, setIsAudioUploaderOpen] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioStates, setAudioStates] = useState({}); // { audio_id: 'not-loaded' | 'loading' | 'ready' | 'error' }
  const [audioProgress, setAudioProgress] = useState({}); // { audio_id: percentage }
  
  // Estados y handlers para sistema de caché offline (IndexedDB) — extraído a `useAudioPreload`
  // Nota: `preloadAudio` (precarga individual para reproducción) sigue aquí porque toca
  // estado compartido con el reproductor (audioStates/audioProgress/playingAudioId).
  // Contador de caché offline (polling 30s) — extraído a `useCacheStats`
  const { cacheStats, setCacheStats, refreshCacheStats } = useCacheStats();
  const {
    cachedAudios,
    downloadingAll,
    downloadProgress,
    checkCachedAudios,
    downloadAllEventAudios,
    clearEventCache,
  } = useAudioPreload({ currentPlaylist, API, refreshCacheStats });

  // Detectar si es móvil/PWA para optimizar
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  // CRÍTICO: Limpiar audios antiguos al cambiar de playlist o montar componente
  useEffect(() => {
    logger.log('🧹 Limpieza preventiva de audios antiguos...');
    let cleaned = 0;
    
    document.querySelectorAll('audio').forEach(audio => {
      // Verificar si el audio está en un estado válido
      const isValid = audio.src && 
                     audio.src !== '' && 
                     audio.readyState > 0 && 
                     !audio.error;
      
      if (!isValid) {
        audio.pause();
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
        audio.remove();
        cleaned++;
      }
    });
    
    if (cleaned > 0) {
      logger.log(`✅ ${cleaned} audios inválidos limpiados`);
      // Resetear estados
      setAudioStates({});
      setAudioProgress({});
      setPlayingAudioId(null);
    }
    
    // Verificar qué audios están en caché para este evento
    if (currentPlaylist?.id) {
      checkCachedAudios();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: checkCachedAudios
  }, [currentPlaylist?.id]); // Se ejecuta al cambiar de playlist
  
  // Verificar qué audios del evento actual están en caché → extraído a `useAudioPreload`
  // (checkCachedAudios expuesto desde el hook)
  
  // Actualizar estadísticas de caché periódicamente → extraído a `useCacheStats` arriba.

  // Función para precargar un audio específico (OPTIMIZADA PARA MÓVIL/PWA)
  const preloadAudio = async (audioFileId) => {
    const audioId = `audio-${audioFileId}`;
    
    logger.log(`🔄 Iniciando precarga de audio: ${audioFileId} (Móvil: ${isMobile}, PWA: ${isPWA})`);
    
    // Si ya existe y está realmente listo, no hacer nada
    const existingAudio = document.getElementById(audioId);
    if (existingAudio && existingAudio.readyState >= 4) {
      logger.log(`✅ Audio ya está precargado: ${audioFileId}`);
      setAudioStates(prev => ({ ...prev, [audioFileId]: 'ready' }));
      return;
    }

    setAudioStates(prev => ({ ...prev, [audioFileId]: 'loading' }));
    setAudioProgress(prev => ({ ...prev, [audioFileId]: 0 }));

    try {
      // PRIORIDAD 1: Intentar desde caché offline (IndexedDB)
      logger.log(`📦 Verificando caché offline para ${audioFileId}...`);
      const cachedData = await getAudioFromCache(audioFileId);
      
      if (cachedData && cachedData.blob) {
        // ✅ AUDIO DESDE CACHÉ - MODO OFFLINE
        logger.log(`✅ Audio recuperado desde caché offline (${Math.round(cachedData.size / 1024)}KB) - Disponible sin internet`);
        setAudioProgress(prev => ({ ...prev, [audioFileId]: 50 }));
        
        const blobUrl = URL.createObjectURL(cachedData.blob);
        
        // Limpiar audio existente
        if (existingAudio) {
          existingAudio.pause();
          if (existingAudio.src && existingAudio.src.startsWith('blob:')) {
            URL.revokeObjectURL(existingAudio.src);
          }
          existingAudio.src = '';
          existingAudio.remove();
        }
        
        // Crear elemento audio
        const audio = document.createElement('audio');
        audio.id = audioId;
        audio.preload = 'auto';
        audio.style.display = 'none';
        
        logger.log(`📦 Usando audio desde caché local (OFFLINE-READY)`);
        
        setAudioProgress(prev => ({ ...prev, [audioFileId]: 75 }));
        
        // Esperar a que esté listo
        const waitForReady = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.error(`⏱️ Timeout esperando audio ${audioFileId}`);
            reject(new Error('Timeout'));
          }, 10000);
          
          audio.addEventListener('canplay', () => {
            clearTimeout(timeout);
            logger.log(`✅ Audio ${audioFileId} listo (desde caché)`);
            resolve();
          }, { once: true });
          
          audio.addEventListener('error', (e) => {
            clearTimeout(timeout);
            console.error(`❌ Error en elemento audio ${audioFileId}:`, audio.error);
            reject(audio.error);
          }, { once: true });
        });
        
        audio.addEventListener('pause', () => {
          if (playingAudioId === audioFileId) {
            setPlayingAudioId(null);
          }
        });
        
        audio.addEventListener('ended', () => {
          setPlayingAudioId(null);
        });
        
        document.body.appendChild(audio);
        audio.src = blobUrl;
        audio.load();
        
        await waitForReady;
        
        setAudioStates(prev => ({ ...prev, [audioFileId]: 'ready' }));
        setAudioProgress(prev => ({ ...prev, [audioFileId]: 100 }));
        logger.log(`🎵 Audio ${audioFileId} listo para reproducir (desde caché)`);
        
      } else {
        // PRIORIDAD 2: Descargar desde servidor
        logger.log(`📡 No está en caché, descargando desde servidor...`);
        
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.error('❌ No hay token disponible');
          setAudioStates(prev => ({ ...prev, [audioFileId]: 'error' }));
          return;
        }
        
        // OPTIMIZACIÓN MÓVIL/iOS: streaming directo del elemento <audio> con ?token=...
        // Iter91.4: ANTES usábamos fetch+blob (que falla en iOS Safari/PWA con
        // "Body is disturbed or locked" para algunos audios). Ahora dejamos que iOS
        // maneje el streaming nativamente con range requests. Auth via query token.
        // El offline-cache se intenta DESPUÉS en background (best-effort).
        if (isMobile || isPWA) {
          logger.log('📱 iOS PWA: streaming directo con ?token= (sin fetch+blob)');

          // Limpiar audio existente
          if (existingAudio) {
            existingAudio.pause();
            if (existingAudio.src && existingAudio.src.startsWith('blob:')) {
              URL.revokeObjectURL(existingAudio.src);
            }
            existingAudio.src = '';
            existingAudio.remove();
          }

          // Limpiar otros audios para liberar memoria
          document.querySelectorAll('audio').forEach(a => {
            if (a.id !== audioId && a.id !== `audio-${playingAudioId}`) {
              a.pause();
              if (a.src.startsWith('blob:')) URL.revokeObjectURL(a.src);
              a.remove();
            }
          });

          // Crear elemento audio
          const audio = document.createElement('audio');
          audio.id = audioId;
          audio.preload = 'auto';
          audio.style.display = 'none';
          // CORS opcional para Safari (no fuerza, pero ayuda con range requests)
          audio.crossOrigin = 'anonymous';

          setAudioProgress(prev => ({ ...prev, [audioFileId]: 50 }));

          // Esperar canplay con timeout 30s (iOS PWA en redes lentas)
          const waitForReady = new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error(`⏱️ Timeout esperando audio ${audioFileId}`);
              reject(new Error('Timeout esperando audio (30s)'));
            }, 30000);

            audio.addEventListener('canplay', () => {
              clearTimeout(timeout);
              logger.log(`✅ Audio ${audioFileId} listo (canplay) — streaming directo`);
              resolve();
            }, { once: true });

            audio.addEventListener('error', () => {
              clearTimeout(timeout);
              const err = audio.error;
              console.error(`❌ Error en elemento audio ${audioFileId}:`, err);
              reject(err || new Error('Error desconocido del elemento audio'));
            }, { once: true });
          });

          audio.addEventListener('pause', () => {
            if (playingAudioId === audioFileId) setPlayingAudioId(null);
          });
          audio.addEventListener('ended', () => setPlayingAudioId(null));

          document.body.appendChild(audio);
          // ✅ Streaming directo — sin fetch, sin blob → sin bug "Body is disturbed or locked"
          audio.src = `${API}/audio/${audioFileId}?token=${encodeURIComponent(token)}`;
          audio.load();

          await waitForReady;

          setAudioStates(prev => ({ ...prev, [audioFileId]: 'ready' }));
          setAudioProgress(prev => ({ ...prev, [audioFileId]: 100 }));
          logger.log(`🎵 Audio ${audioFileId} listo para reproducir (streaming)`);

          // ─── BACKGROUND: cache offline best-effort (no bloquea la UX) ───
          // Si el navegador es iOS y el archivo no es enorme, intentamos cachear
          // para uso offline. Si falla con cualquier error, simplemente lo ignoramos.
          (async () => {
            try {
              const bgController = new AbortController();
              const bgTimeout = setTimeout(() => bgController.abort(), 60000);
              const bgResp = await fetch(
                `${API}/audio/${audioFileId}?token=${encodeURIComponent(token)}`,
                { method: 'GET', signal: bgController.signal }
              );
              clearTimeout(bgTimeout);
              if (!bgResp.ok) return;
              const bgBlob = await bgResp.blob();
              if (bgBlob.size > 0 && bgBlob.size < 60 * 1024 * 1024) {
                const { saveAudioToCache } = await import('../utils/audioCache');
                await saveAudioToCache(audioFileId, bgBlob, currentPlaylist?.id || '', {});
                logger.log(`💾 [bg] Audio cacheado offline: ${audioFileId} (${Math.round(bgBlob.size/1024)}KB)`);
              }
            } catch (bgErr) {
              // Best-effort: si no podemos cachear, no pasa nada — el audio ya está reproduciendo via streaming
              logger.debug(`[bg cache] no se pudo cachear ${audioFileId}:`, bgErr?.message);
            }
          })();

      } else {
        // MÉTODO DESKTOP: Con streaming y progreso detallado
        logger.log('💻 Usando método con streaming para desktop');
        
        const response = await fetch(`${API}/audio/${audioFileId}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          console.error(`❌ HTTP ${response.status} para audio ${audioFileId}`);
          setAudioStates(prev => ({ ...prev, [audioFileId]: 'error' }));
          return;
        }

        const reader = response.body.getReader();
        const contentLength = +response.headers.get('Content-Length');
        let receivedLength = 0;
        const chunks = [];

        logger.log(`📥 Descargando ${contentLength ? Math.round(contentLength / 1024) + 'KB' : 'tamaño desconocido'}...`);

        while(true) {
          const {done, value} = await reader.read();
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          
          if (contentLength) {
            const progress = Math.round((receivedLength / contentLength) * 100);
            setAudioProgress(prev => ({ ...prev, [audioFileId]: progress }));
          }
        }

        logger.log(`✅ Descarga completa: ${Math.round(receivedLength / 1024)}KB`);

        const blob = new Blob(chunks, { type: 'audio/mpeg' });
        
        // Guardar en caché IndexedDB para persistencia offline
        try {
          const { saveAudioToCache } = await import('../utils/audioCache');
          await saveAudioToCache(audioFileId, blob, currentPlaylist?.id || '', {});
          logger.log(`💾 Audio guardado en caché IndexedDB: ${audioFileId}`);
        } catch (cacheErr) {
          console.warn('⚠️ No se pudo guardar en caché IndexedDB:', cacheErr);
        }
        
        const blobUrl = URL.createObjectURL(blob);

        if (existingAudio) {
          existingAudio.pause();
          if (existingAudio.src.startsWith('blob:')) {
            URL.revokeObjectURL(existingAudio.src);
          }
          existingAudio.remove();
        }

        const audio = document.createElement('audio');
        audio.id = audioId;
        audio.preload = 'auto';
        audio.style.display = 'none';
        
        setAudioProgress(prev => ({ ...prev, [audioFileId]: 95 }));

        const waitForReady = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.error(`⏱️ Timeout esperando audio ${audioFileId}`);
            reject(new Error('Timeout'));
          }, 10000);

          audio.addEventListener('canplaythrough', () => {
            clearTimeout(timeout);
            logger.log(`✅ Audio ${audioFileId} REALMENTE listo (canplaythrough)`);
            resolve();
          }, { once: true });

          audio.addEventListener('error', (e) => {
            clearTimeout(timeout);
            console.error(`❌ Error en elemento audio ${audioFileId}:`, audio.error);
            reject(audio.error);
          }, { once: true });
        });

        audio.addEventListener('pause', () => {
          if (playingAudioId === audioFileId) {
            setPlayingAudioId(null);
          }
        });

        audio.addEventListener('ended', () => {
          setPlayingAudioId(null);
        });

        document.body.appendChild(audio);
        audio.src = blobUrl;

        await waitForReady;
        
        setAudioStates(prev => ({ ...prev, [audioFileId]: 'ready' }));
        setAudioProgress(prev => ({ ...prev, [audioFileId]: 100 }));
        logger.log(`🎵 Audio ${audioFileId} listo para reproducir`);
      }
      } // Cierre del else (no está en caché)

    } catch (error) {
      console.error(`❌ Error precargando audio ${audioFileId}:`, error);
      // RCA feb-2026 (deployer): mensajes anteriores atribuían fallos de
      // autorización (403) a "formato no soportado". Ahora, cuando el <audio>
      // falla con MEDIA_ERR_SRC_NOT_SUPPORTED (code 4) en el flujo streaming
      // iOS (audio.src = /api/audio/xxx?token=xxx), hacemos una comprobación
      // rápida del status HTTP real para distinguir 401/403/404 de un fallo
      // de códec verdadero.
      let realStatus = null;
      if ((error?.code === 4 || error?.code === 'MEDIA_ERR_SRC_NOT_SUPPORTED') && (isMobile || isPWA)) {
        try {
          const tok = localStorage.getItem('token');
          const probe = await fetch(`${API}/audio/${audioFileId}?token=${encodeURIComponent(tok || '')}`, {
            method: 'GET', headers: { Range: 'bytes=0-0' }
          });
          realStatus = probe.status;
          logger.log(`🩺 Probe status HTTP tras error 4: ${realStatus}`);
        } catch (probeErr) {
          logger.debug('probe fallo:', probeErr?.message);
        }
      }
      const reason = error?.message || error?.code || 'Error desconocido';
      let friendly;
      if (realStatus === 403) {
        friendly = 'No tienes permiso para reproducir este audio (la cuenta actual no es el DJ ni el asistente del evento).';
      } else if (realStatus === 401) {
        friendly = 'Tu sesión ha caducado. Inicia sesión de nuevo.';
      } else if (realStatus === 404) {
        friendly = 'El archivo ya no existe en el servidor.';
      } else if (error?.code === 4 || error?.code === 'MEDIA_ERR_SRC_NOT_SUPPORTED') {
        friendly = 'No se pudo cargar el audio (formato o permisos). Reintenta o confirma que has iniciado sesión con la cuenta correcta.';
      } else if (error?.code === 3 || error?.code === 'MEDIA_ERR_DECODE') {
        friendly = 'Error decodificando audio (archivo corrupto o codec no soportado)';
      } else {
        friendly = reason;
      }
      toast.error(`No se pudo cargar el audio: ${friendly}`, { duration: 6000 });
      setAudioStates(prev => ({ ...prev, [audioFileId]: 'error' }));
    }
  };

  // Descargar todos los audios y limpiar caché del evento → extraídos a `useAudioPreload`
  // (downloadAllEventAudios, clearEventCache expuestos desde el hook)

  const preloadAllAudios = async () => {
    logger.log('⚠️ preloadAllAudios deshabilitado temporalmente');
    return;
  };

  // TODO: Implementar precarga automática cuando se identifique correctamente la estructura de datos
  // useEffect para precargar se deshabilitó temporalmente por error de referencia
  const [peticionesSearchQuery, setPeticionesSearchQuery] = useState('');
  const [peticionesSearchResults, setPeticionesSearchResults] = useState([]);
  const [peticionesSearching, setPeticionesSearching] = useState(false);
  const [peticionNota, setPeticionNota] = useState('');
  const [buscarEnBiblioteca, setBuscarEnBiblioteca] = useState(true);
  const [buscarEnSpotify, setBuscarEnSpotify] = useState(true);
  const [peticionesPendingFlags, setPeticionesPendingFlags] = useState({}); // Track which peticiones are marked as pending
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddStyleDialogOpen, setIsAddStyleDialogOpen] = useState(false);
  const [isDeleteStyleDialogOpen, setIsDeleteStyleDialogOpen] = useState(false);
  // isAddMomentoDialogOpen / isDeleteMomentoDialogOpen removed (Ola cleanup 2026-02): orphan state.

  // Estados orphan de auto-asignación masiva de estilos removidos (Ola cleanup 2026-02).

  // Handlers de peticiones live (drag-drop, eliminar, ocultar) → extraído a `usePeticionesHandlers`
  const {
    handlePeticionesDragEnd,
    handleRemovePeticion,
    handleTogglePeticionHidden,
  } = usePeticionesHandlers({
    API,
    currentPlaylist,
    setCurrentPlaylist,
    refetchPlaylists,
  });

  const [isSpotifySearchDialogOpen, setIsSpotifySearchDialogOpen] = useState(false);
  // Duplicates detection state + handlers → extraído a `useDuplicatesDetection`
  // (hook se llama más abajo tras declarar fetchSongs — misma técnica que useUrlEventSync)
  // showDuplicatesView permanece en App.js porque es UI-toggle de navegación.
  const [showDuplicatesView, setShowDuplicatesView] = useState(false);
  
  // Shared Notes → extraído a `useSharedNotes` (estado + fetch on playlist change + CRUD)
  const {
    sharedNotes,
    setSharedNotes,
    isNotesDialogOpen,
    setIsNotesDialogOpen,
    currentNote,
    setCurrentNote,
    editingNoteId,
    setEditingNoteId,
    fetchSharedNotes,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    handleEditNote,
  } = useSharedNotes({ API, currentPlaylist });
  const [isPlaylistAnalyzerOpen, setIsPlaylistAnalyzerOpen] = useState(false);

  // (spotifySearchResults/searchingSpotify eliminados: state de handlers muertos)

  // Smart Playlists state
  const [showSmartPlaylistsDialog, setShowSmartPlaylistsDialog] = useState(false);

  // Filtros para Lista Personalizada
  const [listaPersonalizadaSearch, setListaPersonalizadaSearch] = useState("");
  const [listaPersonalizadaFilterGenre, setListaPersonalizadaFilterGenre] = useState("all");
  const [listaPersonalizadaFilterMomento, setListaPersonalizadaFilterMomento] = useState("all");
  const [listaPersonalizadaFilterEnergia, setListaPersonalizadaFilterEnergia] = useState("all");
  const [listaPersonalizadaViewMode, setListaPersonalizadaViewMode] = useState('list'); // 'list', 'sections', or 'energy'

  const [selectedGenreToDelete, setSelectedGenreToDelete] = useState("");
  const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] = useState(false);
  // Fecha pre-rellenada cuando se abre el dialog desde una celda del calendario
  const [createPlaylistInitialDate, setCreatePlaylistInitialDate] = useState(null);
  const [isEditPlaylistOpen, setIsEditPlaylistOpen] = useState(false);
  const [isAddToPlaylistOpen, setIsAddToPlaylistOpen] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const [isApplyTemplateOpen, setIsApplyTemplateOpen] = useState(false);
  const [playlistTemplates, setPlaylistTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({
    nombre: '',
    descripcion: '',
    tipo_evento: '',
    tags: ''
  });

  const [editingSong, setEditingSong] = useState(null);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [customGenres, setCustomGenres] = useState([]);
  const [deletedGenres, setDeletedGenres] = useState([]);
  const [customMomentos, setCustomMomentos] = useState([]);
  const [deletedMomentos, setDeletedMomentos] = useState([]);
  const [newStyleName, setNewStyleName] = useState("");
  const [newMomentoName, setNewMomentoName] = useState("");
  
  // Estados para Precios
  const [precios, setPrecios] = useState([]);
  // editingPrecio state removed (Ola E): moved into PreciosManager.jsx — single source of truth.

  // Estados para Plantillas
  const [templates, setTemplates] = useState([]);
  const [smartPlaylistTemplates, setSmartPlaylistTemplates] = useState([]);

  // Estados para Mis Asignaciones (usuarios no-admin)
  const [myAssignments, setMyAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentFilterEstado, setAssignmentFilterEstado] = useState("");
  const [hidePastAssignments, setHidePastAssignments] = useState(true); // Ocultar pasados por defecto
  const [lastAssignmentsUpdate, setLastAssignmentsUpdate] = useState(null); // Última actualización
  const [hiddenPastCount, setHiddenPastCount] = useState(0); // Eventos pasados ocultos por el filtro

  
  // Estados para Denon Engine DJ
  const [denonPendientesCount, setDenonPendientesCount] = useState(0);

  const [selectedMomentoToDelete, setSelectedMomentoToDelete] = useState("");
  const [generatedCustomList, setGeneratedCustomList] = useState([]);
  const [filteredCustomList, setFilteredCustomList] = useState([]);
  // customListPlayedState: { [song_id]: { played: bool, updated_at: iso } }
  // Persiste en backend como `playlists.custom_list_played_state`. Permite al DJ
  // marcar canciones ya reproducidas durante el evento (feature Feb 2026).
  const [customListPlayedState, setCustomListPlayedState] = useState({});

  // Hidratación automática: cada vez que el evento seleccionado trae un
  // `custom_list_played_state`, lo aplicamos al estado local. Resuelve bug
  // de persistencia-on-load detectado en iteration_45 (el UI mostraba vacío
  // tras reabrir el evento). Reaccionar al ID del evento + al snapshot del
  // state garantiza re-render cuando el backend devuelve el objeto nuevo.
  useEffect(() => {
    if (currentPlaylist?.custom_list_played_state) {
      setCustomListPlayedState(currentPlaylist.custom_list_played_state);
    } else if (currentPlaylist) {
      // currentPlaylist existe pero sin estado → reset (primera apertura)
      setCustomListPlayedState({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: currentPlaylist
  }, [currentPlaylist?.id, currentPlaylist?.custom_list_played_state]);
  
  // Iter89.4: Helper para enriquecer canciones con flags visuales (imperdible, inicio HL, Spotify cliente)
  // Se aplica en el call site antes de pasar a SortableSongList/MomentosSortableView
  // Iter89.16: fallback por song.source para retrocompatibilidad con playlists pre-marcadores
  const enrichSongWithMarkers = useCallback((song, markers) => {
    if (!song) return song;
    const sid = song.id;
    if (!sid) return song;
    const m = markers || {};
    return {
      ...song,
      _imprescindible: Array.isArray(m.imprescindibles_ids) && m.imprescindibles_ids.includes(sid),
      _inicio_hora_loca: m.inicio_hora_loca_id === sid,
      // El badge "Playlist Cliente" refleja SOLO la lista de Spotify aportada por el cliente
      // PARA ESTE EVENTO (playlist_markers.cliente_spotify_ids). NO se deduce del `source`
      // histórico de la canción en biblioteca: una canción importada una vez de Spotify de un
      // cliente queda con source 'cliente_spotify_pendiente_denon'/'biblioteca_cliente_spotify'
      // PARA SIEMPRE, y antes se pintaba con el badge en todos los eventos futuros aunque no
      // hubiera lista de cliente (bug "The Grease Megamix").
      _de_spotify_cliente: (
        Array.isArray(m.cliente_spotify_ids) && m.cliente_spotify_ids.includes(sid)
      ),
    };
  }, []);

  // FIX badges DJ: garantizar que currentPlaylist SIEMPRE tenga playlist_markers.
  // Los badges "Imperdible" e "Inicio Hora Loca" dependen exclusivamente de playlist_markers,
  // mientras que "Spotify Cliente" tiene fallback por source. Si un update optimista, la caché
  // offline o un flujo del DJ deja currentPlaylist sin markers, esos 2 badges desaparecían (solo
  // se veía Spotify Cliente). Aquí los recuperamos del backend cuando faltan.
  const markersInFlightRef = useRef(false);
  useEffect(() => {
    const pid = currentPlaylist?.id;
    if (!pid || currentPlaylist.playlist_markers || markersInFlightRef.current) return;
    markersInFlightRef.current = true;
    axios.get(`${API}/playlists/${pid}`)
      .then(res => {
        const mk = res.data?.playlist_markers;
        if (mk) {
          setCurrentPlaylist(prev => (prev && prev.id === pid ? { ...prev, playlist_markers: mk } : prev));
        }
      })
      .catch(() => {})
      .finally(() => { markersInFlightRef.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlaylist?.id, currentPlaylist?.playlist_markers]);

  const [showWhatsAppImporter, setShowWhatsAppImporter] = useState(false);
  const [showClientTextImporter, setShowClientTextImporter] = useState(false);
  const [showEventWizard, setShowEventWizard] = useState(false);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);
  
  // Mix Suggestions Modal state
  const [isMixSuggestionsOpen, setIsMixSuggestionsOpen] = useState(false);
  const [mixSuggestionsSong, setMixSuggestionsSong] = useState(null);
  
  // Mix Chains Manager state
  const [isMixChainsOpen, setIsMixChainsOpen] = useState(false);
  const [mixChainsMode, setMixChainsMode] = useState('browse'); // 'browse' | 'create'
  const [selectedSongsForChain, setSelectedSongsForChain] = useState([]);
  
  const [currentTab, setCurrentTab] = useState("lista");
  const [loadedTabs, setLoadedTabs] = useState(new Set(['lista'])); // Track loaded tabs for lazy loading

  // Sincronización URL ↔ estado de navegación → extraído a `useUrlEventSync`
  // (procesa ?view=, ?playlistId=, ?openEvent=, ?liveMode= desde rutas externas)
  // Colocado aquí porque necesita setters declarados arriba (setGeneratedCustomList, setLoadedTabs, setCurrentTab)
  useUrlEventSync({
    API,
    isAuthenticated,
    location,
    navigate,
    currentPlaylist,
    setCurrentPlaylist,
    setGeneratedCustomList,
    setFilteredCustomList,
    setView,
    setIsLiveMode,
    setLoadedTabs,
    setCurrentTab,
  });
  
  // Smart cache for Spotify searches and frequent songs
  const [spotifySearchCache, setSpotifySearchCache] = useState(new Map());
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

  // Local state for add dialog search
  const [searchGenre, setSearchGenre] = useState(""); // Para filtro en diálogo de añadir
  const [addDialogFilterRating, setAddDialogFilterRating] = useState(""); // Filtro de rating en diálogo
  const [addDialogShowFavoritesOnly, setAddDialogShowFavoritesOnly] = useState(false); // Solo favoritos en diálogo
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    genres: [],
    bpm: "",
    year: "",
    energia: "",
    rating: null,
    momentos: [],
    notas: "",
    favorito: false,
    pendiente_denon: false
  });
  
  // Store Spotify access token globally for queue function
  useEffect(() => {
    window.spotifyAccessToken = accessToken;
  }, [accessToken]);

  // Create global function for adding to Spotify queue
  // Enhancement 2026-04-20: debouncing — al añadir varias canciones en ráfaga,
  // consolidar en UN toast con contador en lugar de N toasts apilados.
  const queueBatchRef = useRef({ timer: null, successes: [] });
  useEffect(() => {
    const flushBatch = () => {
      const { successes } = queueBatchRef.current;
      queueBatchRef.current.successes = [];
      queueBatchRef.current.timer = null;
      if (successes.length === 0) return;
      if (successes.length === 1) {
        toast.success(`"${successes[0].title}" añadida a la cola de Spotify`);
      } else {
        const preview = successes.slice(0, 3).map(s => s.title).join(', ');
        const extra = successes.length > 3 ? `, +${successes.length - 3} más` : '';
        toast.success(`${successes.length} canciones añadidas a la cola`, {
          description: `${preview}${extra}`
        });
      }
    };

    window.addToSpotifyQueue = async (spotifyUri, title, artist) => {
      try {
        const token = window.spotifyAccessToken;
        
        if (!token) {
          toast.error('Conecta tu cuenta de Spotify primero');
          return;
        }
        
        const response = await axios.post(
          `${API}/spotify/queue/add`,
          { 
            track_uri: spotifyUri,
            access_token: token
          }
        );
        
        if (response.data.status === 'success') {
          // Batch success toasts con debounce 500ms
          queueBatchRef.current.successes.push({ title, artist });
          if (queueBatchRef.current.timer) clearTimeout(queueBatchRef.current.timer);
          queueBatchRef.current.timer = setTimeout(flushBatch, 500);
        }
      } catch (error) {
        console.error('Error adding to Spotify queue:', error);
        // Los errores NO se agrupan — cada uno es una acción fallida que el DJ debe ver.
        // El backend devuelve detail accionable (404 sin device, 403 no-Premium, 401 expirado).
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.length > 0) {
          toast.error(detail);
        } else {
          toast.error('Error al añadir a la cola de Spotify');
        }
      }
    };
    
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref cleanup pattern (intentional)
      if (queueBatchRef.current.timer) clearTimeout(queueBatchRef.current.timer);
      delete window.addToSpotifyQueue;
      delete window.spotifyAccessToken;
    };
  }, []);

  // Load local data on mount
  useEffect(() => {
    if (isAuthenticated) {
      // (reset de isDetectingDuplicates ya no es necesario: `useDuplicatesDetection`
      // lo inicializa a `false` en cada mount)

      // Clear any problematic localStorage entries
      localStorage.removeItem('duplicateDetectionInProgress');
      
      // Load custom genres from localStorage first
      const savedGenres = localStorage.getItem('customGenres');
      if (savedGenres) {
        setCustomGenres(JSON.parse(savedGenres));
      } else {
        // If no localStorage genres, try to load from backend
        axios.get(`${API}/genres`).then(response => {
          if (response.data.genres && response.data.genres.length > 0) {
            setCustomGenres(response.data.genres);
            localStorage.setItem('customGenres', JSON.stringify(response.data.genres));
            logger.log('✅ Loaded genres from backend:', response.data.genres.length);
          }
        }).catch(e => {
          logger.log('Could not load genres from backend:', e.message);
        });
      }
      const savedDeletedGenres = localStorage.getItem('deletedGenres');
      if (savedDeletedGenres) {
        setDeletedGenres(JSON.parse(savedDeletedGenres));
      }
      
      // Load custom momentos and deleted momentos from localStorage
      const savedMomentos = localStorage.getItem('customMomentos');
      if (savedMomentos) {
        setCustomMomentos(JSON.parse(savedMomentos));
      }
      const savedDeletedMomentos = localStorage.getItem('deletedMomentos');
      if (savedDeletedMomentos) {
        setDeletedMomentos(JSON.parse(savedDeletedMomentos));
      }
      
      // Fetch Denon count
      fetchDenonPendientesCount();
    }
  }, [isAuthenticated]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filterGenre, filterRating, filterMomento, filterEnergia, showFavoritesOnly, showOnlyDenonPendientes]);

  // 🐛 Fix bug (2026-05-20): la vista "Lista" del detalle del evento usa
  // `filteredCustomList` (state local), pero varias rutas abren el evento sin
  // sincronizarlo (InformesView, búsqueda global, URL deep-link, etc.). Al
  // cambiar de `currentPlaylist` cargamos los temas desde el endpoint by-ids
  // para que las vistas Lista/Momentos/Energía sean coherentes.
  useEffect(() => {
    const ids = currentPlaylist?.lista_personalizada_generada;
    if (!ids || ids.length === 0) {
      setGeneratedCustomList([]);
      setFilteredCustomList([]);
      return;
    }
    // Evita refetch si ya tenemos los temas correctos cargados
    if (filteredCustomList.length === ids.length) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/songs/by-ids`, { ids });
        if (cancelled) return;
        setGeneratedCustomList(res.data.songs);
        setFilteredCustomList(res.data.songs);
      } catch (err) {
        console.error('[CustomList sync] Error loading songs by ids:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: currentPlaylist?
  }, [currentPlaylist?.id, currentPlaylist?.lista_personalizada_generada?.length]);

  // Fetch Denon count when songs change
  useEffect(() => {
    if (songs.length > 0) {
      const fetchCount = async () => {
        try {
          const response = await axios.get(`${API}/songs/stats/denon-pendientes`);
          setDenonPendientesCount(response.data.count);
        } catch (error) {
          console.error("Error al cargar conteo Denon:", error);
        }
      };
      fetchCount();
    }
  }, [songs]);

  // Load user assignments if not admin
  // Ref mantiene los filtros actuales para que el setInterval no use closure obsoleta
  const assignmentFiltersRef = useRef({ assignmentFilterEstado: "", hidePastAssignments: true });
  useEffect(() => {
    assignmentFiltersRef.current = { assignmentFilterEstado, hidePastAssignments };
  }, [assignmentFilterEstado, hidePastAssignments]);

  useEffect(() => {
    if (user && user.role !== 'admin' && view === "dashboard") {
      loadMyAssignments();
      
      // Auto-refresh every 60 seconds for non-admin users (usa filtros vigentes vía ref)
      const interval = setInterval(() => {
        const { assignmentFilterEstado: estado, hidePastAssignments: hidePast } = assignmentFiltersRef.current;
        loadMyAssignments(estado, hidePast);
      }, 60000);
      
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadMyAssignments
  }, [user, view]);

  // `notify`: solo la recarga manual (botón Actualizar) muestra toast; las cargas automáticas
  // (al entrar y cada 60 s) son silenciosas para no interrumpir al DJ durante el evento.
  const loadMyAssignments = async (estado = "", hidePast = hidePastAssignments, { notify = false } = {}) => {
    setLoadingAssignments(true);
    try {
      const params = new URLSearchParams();
      if (estado) params.append('estado', estado);
      
      const response = await axios.get(`${API}/users/my-assignments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let assignments = response.data.assignments || [];
      
      // Filtrar eventos pasados si está activado
      let hiddenPast = 0;
      if (hidePast) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const total = assignments.length;
        assignments = assignments.filter(a => {
          if (!a.fecha_evento) return true;
          const eventDate = new Date(a.fecha_evento);
          return eventDate >= today;
        });
        hiddenPast = total - assignments.length;
      }
      
      setHiddenPastCount(hiddenPast);
      setMyAssignments(assignments);
      setLastAssignmentsUpdate(new Date());
      if (notify) toast.success(`${assignments.length} asignaciones cargadas`);
    } catch (error) {
      console.error("Error cargando asignaciones:", error);
      // En auto-refresh con red mala conservamos la última lista conocida y no molestamos
      if (notify) toast.error("Error al cargar tus asignaciones");
    } finally {
      setLoadingAssignments(false);
    }
  };

  // Sincronizar automáticamente cuando vuelva la conexión
  useEffect(() => {
    const handleOnline = async () => {
      logger.log('🟢 Conexión restaurada - Sincronizando cambios pendientes...');
      toast.info('🔄 Sincronizando cambios pendientes...');
      
      const success = await syncPendingChanges(axios);
      
      if (success) {
        toast.success('✅ Cambios sincronizados correctamente');
        // Recargar datos
        fetchSongs();
        fetchPlaylists();
      } else {
        toast.warning('⚠️ Algunos cambios no se pudieron sincronizar');
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchPlaylists and fetchSongs
  }, [syncPendingChanges]);

  // Registrar Background Sync al cargar la app
  useEffect(() => {
    if (user) {
      registerBackgroundSync();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: registerBackgroundSync
  }, [user]);

  // Precargar eventos próximos cuando se cargan los playlists
  useEffect(() => {
    if (playlists.length > 0) {
      precacheUpcomingEvents(playlists);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: precacheUpcomingEvents
  }, [playlists]);

  // Load shared notes when playlist changes → gestionado por useSharedNotes internamente

  // Load duplicates when view is shown → gestionado por useDuplicatesDetection

  
  // No redirect for non-admin users - they see their assignments now
  // Auth gate ya manejada en MainApp; aquí no hay early returns para evitar
  // Rules-of-Hooks violations.

  // filteredPlaylists viene del contexto con búsqueda accent-insensitive

  // Handlers de detección/borrado de duplicados → extraídos a `useDuplicatesDetection`
  // (loadDuplicates, handleDeleteDuplicate, handleRemoveDuplicates, handleDeleteAllDuplicates)

  // Shared Notes handlers (fetch/create/update/delete/edit) → extraídos a `useSharedNotes` arriba.

  // Auto-asignar estilo a una canción individual usando Spotify
  const handleAutoAssignStyle = async (song, spotifyAccessToken, userCustomGenres) => {
    try {
      toast.info(`🎨 Buscando estilo para "${song.title}"...`);
      
      const response = await axios.post(`${API}/songs/${song.id}/auto-assign-style`, {
        access_token: spotifyAccessToken,
        custom_genres: userCustomGenres || customGenres
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchSongs();
        
        // If there were also unmatched suggestions, notify
        if (response.data.unmatched_suggestions?.length > 0) {
          toast.info(`💡 La IA también sugirió: ${response.data.unmatched_suggestions.join(', ')} (no están en tu lista)`);
        }
      } else {
        // No match found - show message but don't ask user interactively
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error('Error auto-assigning style:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al asignar estilo: ${errorMsg}`);
    }
  };

  // Auto-asignar estilos masivamente con preview
  const handleBulkAutoAssignStyle = async () => {
    try {
      logger.log('🎨 handleBulkAutoAssignStyle called - Aplicando estilos directamente');
      
      // Check if we have custom genres
      if (!customGenres || customGenres.length === 0) {
        console.warn('⚠️ No custom genres available');
        toast.error('No hay géneros personalizados configurados. Por favor, añade géneros primero.');
        return;
      }
      
      const BATCH_SIZE = 50; // Process 50 songs per batch for better progress feedback
      let offset = 0;
      let totalProcessed = 0;
      let totalAssigned = 0;
      let totalWithoutStyle = 0;
      let batchNumber = 1;
      let shouldContinue = true;
      
      toast.info('🎨 Aplicando estilos automáticamente...');
      
      logger.log('📡 Starting batch processing with direct apply...');
      logger.log('📋 Custom genres to use:', customGenres);
      
      // Process in batches - apply directly (preview_only = false)
      while (shouldContinue) {
        try {
          logger.log(`📦 Processing batch ${batchNumber} (offset: ${offset})`);
          
          const response = await axios.post(`${API}/songs/bulk-auto-assign-style`, {
            custom_genres: customGenres,
            preview_only: false,  // Apply directly, no preview
            batch_size: BATCH_SIZE,
            offset: offset
          });
          
          logger.log(`✅ Batch ${batchNumber} response:`, response.data);
          
          totalWithoutStyle = response.data.total_without_style;
          const processedInBatch = response.data.processed_in_batch;
          const assignedInBatch = response.data.applied || 0;
          const remaining = response.data.remaining;
          
          totalProcessed += processedInBatch;
          totalAssigned += assignedInBatch;
          
          // Show progress
          const progress = totalWithoutStyle > 0 ? Math.round((totalProcessed / totalWithoutStyle) * 100) : 100;
          toast.info(`📊 Lote ${batchNumber}: ${assignedInBatch} estilos asignados (${progress}% completado)`, {
            duration: 2000
          });
          
          // Check if we should continue
          if (remaining === 0 || processedInBatch === 0) {
            shouldContinue = false;
            logger.log('✅ All batches processed');
          } else {
            offset += BATCH_SIZE;
            batchNumber++;
            
            // Small delay between batches
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
        } catch (batchError) {
          console.error(`❌ Error in batch ${batchNumber}:`, batchError);
          toast.error(`Error en lote ${batchNumber}: ${batchError.response?.data?.detail || batchError.message}`);
          shouldContinue = false;
        }
      }
      
      logger.log('✅ All batches completed:', { totalProcessed, totalAssigned });
      
      // Show final result
      toast.success(`✅ Proceso completado: ${totalAssigned} estilos asignados a ${totalProcessed} canciones`);
      
      // Refresh song list to show updated styles
      fetchSongs();
      
    } catch (error) {
      console.error('❌ Error in bulk auto-assign:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Error al procesar canciones: ${error.response?.data?.detail || error.message}`);
    }
  };

  // ==================== AI Genre Analysis ====================
  // handleAnalyzeAIGenres eliminado: código muerto (sin invocaciones en el codebase).

  // handleDownloadExcel eliminado: código muerto (sin invocaciones en el codebase).

  const handleDownloadPDF = async () => {
    try {
      const sampleSize = 200;
      toast.info('📥 Generando archivo PDF...');
      
      const response = await axios.get(`${API}/songs/export-ai-genres-pdf?sample_size=${sampleSize}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'analisis_generos_ia.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('✅ PDF descargado correctamente!');
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };

  // detectDuplicates y detectExactDuplicates → extraídos a `useDuplicatesDetection`

  // Playlist form - now handled by PlaylistForm component

  const getAllGenres = () => {
    // Combinar géneros del backend + géneros custom locales
    const backendGenres = availableGenres || [];
    const genres = new Set([...backendGenres, ...INITIAL_GENRES, ...customGenres]);
    const filtered = Array.from(genres).filter(genre => !deletedGenres.includes(genre));
    const sorted = filtered.sort((a, b) => a.localeCompare(b, 'es'));
    return sorted;
  };

  const getAllMomentos = () => {
    const momentos = new Set([...MOMENTOS_BODA, ...customMomentos]);
    const filtered = Array.from(momentos).filter(momento => !deletedMomentos.includes(momento));
    return filtered.sort((a, b) => a.localeCompare(b, 'es'));
  };

  // loadCustomGenres eliminado: código muerto (sin invocaciones en el codebase).

  const saveCustomGenre = (genre) => {
    logger.log('📝 Saving custom genre:', genre);
    logger.log('📚 Current customGenres:', customGenres);
    const updated = [...customGenres, genre];
    logger.log('✅ Updated customGenres:', updated);
    setCustomGenres(updated);
    localStorage.setItem('customGenres', JSON.stringify(updated));
    logger.log('💾 Saved to localStorage');
  };

  const saveCustomMomento = (momento) => {
    const updated = [...customMomentos, momento];
    setCustomMomentos(updated);
    localStorage.setItem('customMomentos', JSON.stringify(updated));
  };

  const deleteMomento = (momento) => {
    // Remove from customMomentos if it exists
    const updatedCustomMomentos = customMomentos.filter(m => m !== momento);
    setCustomMomentos(updatedCustomMomentos);
    localStorage.setItem('customMomentos', JSON.stringify(updatedCustomMomentos));

    // Add to deletedMomentos list
    const updatedDeletedMomentos = [...deletedMomentos, momento];
    setDeletedMomentos(updatedDeletedMomentos);
    localStorage.setItem('deletedMomentos', JSON.stringify(updatedDeletedMomentos));
  };

  // Alias para compatibilidad - ahora usa React Query
  const fetchSongs = () => {
    refetchSongs();
  };

  // Duplicates detection (llamado tras declarar `fetchSongs` por dependencia)
  const {
    isDuplicatesDialogOpen,
    setIsDuplicatesDialogOpen,
    duplicateGroups,
    setDuplicateGroups,
    isDetectingDuplicates,
    duplicatesLoaded,
    duplicatesError,
    selectedToKeepMap,
    setSelectedToKeepMap,
    detectDuplicates,
    detectExactDuplicates,
    handleDeleteDuplicate,
    handleDeleteAllDuplicates,
  } = useDuplicatesDetection({ API, showDuplicatesView, fetchSongs });

  // Alias para compatibilidad - ahora usa React Query
  const fetchPlaylists = () => {
    refetchPlaylists();
  };

  const fetchPlaylistSongs = async (playlistId) => {
    try {
      const response = await axios.get(`${API}/playlists/${playlistId}/songs`);
      setPlaylistSongs(response.data);
    } catch (error) {
      // 404 = playlist no existe en backend (caché offline desactualizada).
      // Tratamos como "sin canciones" en silencio para no manchar consola/UX.
      if (error?.response?.status === 404) {
        setPlaylistSongs([]);
        return;
      }
      console.error("Error al cargar canciones de playlist:", error);
      toast.error("Error al cargar canciones");
    }
  };

  const fetchPrecios = async () => {
    try {
      const response = await axios.get(`${API}/precios`);
      setPrecios(response.data.precios || []);
    } catch (error) {
      console.error("Error al cargar precios:", error);
      toast.error("Error al cargar precios");
    }
  };

  const loadDelicattoServices = async () => {
    try {
      const response = await axios.post(`${API}/precios/delicatto/initialize`);
      if (response.data.success) {
        toast.success(`✅ ${response.data.message} (${response.data.count} servicios)`);
        fetchPrecios(); // Reload prices
      }
    } catch (error) {
      console.error("Error loading Delicatto services:", error);
      toast.error("Error al cargar servicios de Delicatto");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/event-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error al cargar plantillas:", error);
      toast.error("Error al cargar plantillas");
    }
  };

  const fetchSmartPlaylistTemplates = async () => {
    try {
      const response = await axios.get(`${API}/smart-playlists`);
      setSmartPlaylistTemplates(response.data.playlists || []);
    } catch (error) {
      console.error("Error al cargar plantillas de playlists inteligentes:", error);
      toast.error("Error al cargar plantillas de playlists inteligentes");
    }
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      await axios.post(`${API}/event-templates`, templateData);
      fetchTemplates();
      toast.success("Plantilla creada correctamente");
    } catch (error) {
      console.error("Error al crear plantilla:", error);
      toast.error("Error al crear plantilla");
    }
  };

  // Lazy loading: load tab data only when accessed
  const handleTabChange = async (newTab) => {
    setCurrentTab(newTab);
    
    // Check if tab data is already loaded
    if (!loadedTabs.has(newTab)) {
      logger.log(`[LAZY LOAD] Loading data for tab: ${newTab}`);
      
      try {
        // Load data based on tab
        switch(newTab) {
          case 'biblioteca':
            // Songs are already loaded globally, no extra fetch needed
            break;
          case 'documentos':
            // Documents come with the playlist, no extra fetch needed
            break;
          case 'presupuesto':
            if (precios.length === 0) {
              await fetchPrecios();
            }
            break;
          case 'contrato':
            // Contract data comes with playlist, no extra fetch
            break;
          default:
            // Other tabs don't require extra data loading
            break;
        }
        
        // Mark tab as loaded
        setLoadedTabs(prev => new Set([...prev, newTab]));
        logger.log(`[LAZY LOAD] Tab ${newTab} loaded successfully`);
      } catch (error) {
        console.error(`[LAZY LOAD] Error loading tab ${newTab}:`, error);
      }
    }
  };

  // Cache helper functions
  const getCachedData = (cache, key) => {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
      logger.log(`[CACHE HIT] ${key}`);
      return cached.data;
    }
    if (cached) {
      logger.log(`[CACHE EXPIRED] ${key}`);
    }
    return null;
  };

  const setCachedData = (setCache, key, data) => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(key, { data, timestamp: Date.now() });
      
      // Limit cache size to 100 entries
      if (newCache.size > 100) {
        const firstKey = newCache.keys().next().value;
        newCache.delete(firstKey);
      }
      
      logger.log(`[CACHE SET] ${key}`);
      return newCache;
    });
  };

  // Cached Spotify search
  const cachedSpotifySearch = async (query) => {
    const cacheKey = `spotify_search_${query}`;
    const cached = getCachedData(spotifySearchCache, cacheKey);
    
    if (cached) {
      return cached;
    }
    
    // Make actual API call
    const response = await axios.get(`${API}/spotify/search`, {
      params: { q: query }
    });
    
    setCachedData(setSpotifySearchCache, cacheKey, response.data);
    return response.data;
  };

  // Export lista personalizada to Spotify
  const handleExportToSpotify = async () => {
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('No hay evento seleccionado');
      return;
    }
    
    if (!accessToken) {
      toast.error('Conecta con Spotify primero para exportar');
      return;
    }
    
    if (!currentPlaylist.lista_personalizada_generada || currentPlaylist.lista_personalizada_generada.length === 0) {
      toast.error('La lista personalizada está vacía');
      return;
    }
    
    try {
      toast.info('Creando playlist en Spotify...');
      
      const response = await axios.post(
        `${API}/playlists/${currentPlaylist.id}/export-to-spotify`,
        null,
        {
          params: { access_token: accessToken }
        }
      );
      
      logger.log('[SPOTIFY EXPORT] Success:', response.data);
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">¡Playlist creada en Spotify!</span>
          <span className="text-sm">{response.data.tracks_added} canciones añadidas</span>
          <a 
            href={response.data.playlist_url} 
            target="_blank" 
            rel="noreferrer"
            className="text-green-400 underline hover:text-green-300"
          >
            Abrir en Spotify →
          </a>
        </div>,
        { duration: 4000 }
      );
      
    } catch (error) {
      console.error('[SPOTIFY EXPORT] Error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error al exportar';
      toast.error(`Error al exportar a Spotify: ${errorMsg}`);
    }
  };

  // Group songs by momentos for lista personalizada
  // Cada canción se asigna SOLO al primer momento que coincida para evitar duplicados
  // Función para calcular el momento basado en la posición
  const getMomentoByPosition = (position, totalSongs) => {
    if (totalSongs === 0) return 'Sin Momento';
    
    // Porcentajes: Primera Baile 25%, Baile General 40%, Hora Loca 20%, Cierre 15%
    const percentages = [
      { momento: 'Primera Baile', percent: 0.25 },
      { momento: 'Baile General', percent: 0.40 },
      { momento: 'Hora Loca', percent: 0.20 },
      { momento: 'Cierre', percent: 0.15 }
    ];
    
    let accumulated = 0;
    for (const { momento, percent } of percentages) {
      accumulated += percent;
      if (position / totalSongs < accumulated) {
        return momento;
      }
    }
    return 'Cierre'; // Default al último
  };

  const groupSongsByMomentos = (songIds) => {
    if (!songIds || songIds.length === 0) return {};
    
    const momentoOrder = ['Primera Baile', 'Baile General', 'Hora Loca', 'Cierre'];
    const grouped = {};
    
    // Initialize groups in order
    momentoOrder.forEach(m => grouped[m] = []);
    
    // Use generatedCustomList first, then fall back to songs
    const songsSource = generatedCustomList.length > 0 ? generatedCustomList : 
                        filteredCustomList.length > 0 ? filteredCustomList : songs;
    
    const totalSongs = songIds.length;
    
    // Asignar cada canción al momento según su POSICIÓN en la lista
    const markers = currentPlaylist?.playlist_markers;
    songIds.forEach((songId, index) => {
      const song = songsSource.find(s => s.id === songId);
      if (!song) return;
      
      // Calcular momento dinámicamente por posición (index + 1 porque posición empieza en 1)
      const momento = getMomentoByPosition(index + 1, totalSongs);
      
      // Añadir la canción con su posición global para referencia + flags de markers
      const enriched = enrichSongWithMarkers(song, markers);
      grouped[momento].push({
        ...enriched,
        _posicionGlobal: index + 1,
        _momentoDinamico: momento
      });
    });
    
    // Remove empty groups
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });
    
    return grouped;
  };

  // Handler for reordering custom list with SortableSongList component
  const handleReorderCustomList = async (newOrderedSongs) => {
    const newOrderIds = newOrderedSongs.map(song => song.id);

    // OPTIMISTIC UPDATE: Actualizar TODOS los estados locales PRIMERO
    const updatedPlaylist = {
      ...currentPlaylist,
      lista_personalizada_generada: newOrderIds
    };
    setCurrentPlaylist(updatedPlaylist);

    // Actualizar también las listas de visualización con los objetos completos
    setFilteredCustomList(newOrderedSongs);
    setGeneratedCustomList(newOrderedSongs);

    // Update in backend
    try {
      await axios.put(`${API}/playlists/${currentPlaylist.id}`, {
        lista_personalizada_generada: newOrderIds
      });
      logger.log('✅ [DRAG & DROP] Custom list order updated successfully');
    } catch (error) {
      console.error('❌ [DRAG & DROP] Error updating custom list order:', error);
      toast.error('Error al actualizar el orden');
      // Revert on error: recargar desde el backend
      try {
        const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
        setCurrentPlaylist(response.data);
        
        // Reload songs
        if (response.data.lista_personalizada_generada && response.data.lista_personalizada_generada.length > 0) {
          const songsResponse = await axios.post(`${API}/songs/by-ids`, {
            ids: response.data.lista_personalizada_generada
          });
          setGeneratedCustomList(songsResponse.data.songs);
          setFilteredCustomList(songsResponse.data.songs);
        }
      } catch (revertError) {
        console.error('Error reverting changes:', revertError);
      }
    }
  };

  // Toggle "reproducida" de una canción de la lista personalizada.
  // Feature Feb 2026: permite al DJ marcar durante el evento qué canciones
  // ya ha puesto, con persistencia en backend (`custom_list_played_state`).
  const handleToggleCustomListPlayed = async (songId) => {
    if (!currentPlaylist?.id || !songId) return;

    const wasPlayed = !!customListPlayedState[songId]?.played;
    const nextPlayed = !wasPlayed;

    // Optimistic update
    setCustomListPlayedState((prev) => ({
      ...prev,
      [songId]: { played: nextPlayed, updated_at: new Date().toISOString() },
    }));

    try {
      await axios.put(
        `${API}/playlists/${currentPlaylist.id}/custom-list-played`,
        { song_id: songId, played: nextPlayed }
      );

      // Pill feedback en Modo En Vivo: resumen rápido "Marcada · Quedan N · X%"
      // Se reemplaza (mismo id) para no acumular toasts al marcar en ráfaga.
      if (isLiveMode) {
        const total = (generatedCustomList || []).length;
        // Contamos sobre el estado recién actualizado (incluye este toggle)
        let playedCount = 0;
        for (const s of generatedCustomList || []) {
          const entry = s.id === songId
            ? { played: nextPlayed }
            : customListPlayedState[s.id];
          if (entry?.played) playedCount += 1;
        }
        const remaining = Math.max(0, total - playedCount);
        const percent = total > 0 ? Math.round((playedCount / total) * 100) : 0;

        const prefix = nextPlayed ? '✓ Marcada' : '↺ Desmarcada';
        toast.info(
          `${prefix} · Quedan ${remaining} · ${percent}%`,
          { id: 'mark-feedback', duration: 2000 }
        );
      }
    } catch (error) {
      console.error('Error toggling played state:', error);
      toast.error('No se pudo guardar la marca');
      // Rollback
      setCustomListPlayedState((prev) => ({
        ...prev,
        [songId]: { played: wasPlayed, updated_at: new Date().toISOString() },
      }));
    }
  };

  // Reinicia TODAS las marcas de "reproducida" de la lista personalizada.
  // Útil para reusar la misma lista en otro evento.
  const handleResetCustomListPlayed = async () => {
    if (!currentPlaylist?.id) return;
    const anyPlayed = Object.values(customListPlayedState).some((s) => s?.played);
    if (!anyPlayed) {
      toast.info('No hay canciones marcadas');
      return;
    }
    if (!window.confirm('¿Seguro que quieres desmarcar todas las canciones reproducidas?')) {
      return;
    }

    const snapshot = customListPlayedState;
    setCustomListPlayedState({});

    try {
      await axios.post(
        `${API}/playlists/${currentPlaylist.id}/custom-list-played/reset`
      );
      toast.success('Marcas reiniciadas');
    } catch (error) {
      console.error('Error resetting played state:', error);
      toast.error('Error al reiniciar marcas');
      setCustomListPlayedState(snapshot);
    }
  };

  // Handler for reordering within a specific momento
  // handleReorderMomentoSongs eliminado: código muerto (sin invocaciones en el codebase).

  // Handler para reordenamiento global desde la vista de Momentos (drag-and-drop entre secciones)
  const handleReorderMomentosGlobal = async (newOrderIds) => {
    // Blindaje anti-truncado: reconstruimos los objetos desde un POOL combinado
    // de todas las fuentes (generated + filtered + songs), deduplicado por id.
    // Así ningún id se pierde aunque alguna de esas listas esté filtrada en el
    // futuro (la lista_personalizada_generada que persiste es la verdad).
    const pool = [
      ...(generatedCustomList || []),
      ...(filteredCustomList || []),
      ...(Array.isArray(songs) ? songs : []),
    ];
    const byId = new Map();
    for (const s of pool) {
      if (s && s.id && !byId.has(s.id)) byId.set(s.id, s);
    }
    const allSongs = newOrderIds.map(id => byId.get(id)).filter(Boolean);

    // OPTIMISTIC UPDATE: Actualizar todos los estados locales primero
    const updatedPlaylist = {
      ...currentPlaylist,
      lista_personalizada_generada: newOrderIds
    };
    setCurrentPlaylist(updatedPlaylist);
    // Solo machacamos las listas de objetos locales si la reconstrucción está
    // COMPLETA. Si faltara algún objeto en memoria, NO dejamos una lista parcial
    // (se recargará desde el backend más abajo) para no truncar la vista local.
    const reconstructionComplete = allSongs.length === newOrderIds.length;
    if (reconstructionComplete) {
      setFilteredCustomList(allSongs);
      setGeneratedCustomList(allSongs);
    }
    
    logger.log('🔄 [MOMENTOS] Reordenamiento global:', { totalSongs: newOrderIds.length, reconstructionComplete });

    // Update in backend
    try {
      await axios.put(`${API}/playlists/${currentPlaylist.id}`, {
        lista_personalizada_generada: newOrderIds
      });
      logger.log('✅ [MOMENTOS] Orden actualizado correctamente');
      toast.success('Orden actualizado');
      // Si la reconstrucción local fue incompleta, recargamos los objetos
      // completos desde el backend para reflejar el orden sin perder canciones.
      if (!reconstructionComplete && newOrderIds.length > 0) {
        const songsResponse = await axios.post(`${API}/songs/by-ids`, { ids: newOrderIds });
        const ordered = newOrderIds
          .map(id => songsResponse.data.songs.find(s => s.id === id))
          .filter(Boolean);
        setGeneratedCustomList(ordered);
        setFilteredCustomList(ordered);
      }
    } catch (error) {
      console.error('❌ [MOMENTOS] Error actualizando orden:', error);
      toast.error('Error al actualizar el orden');
      // Revert on error
      try {
        const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
        setCurrentPlaylist(response.data);
        
        if (response.data.lista_personalizada_generada && response.data.lista_personalizada_generada.length > 0) {
          const songsResponse = await axios.post(`${API}/songs/by-ids`, {
            ids: response.data.lista_personalizada_generada
          });
          setGeneratedCustomList(songsResponse.data.songs);
          setFilteredCustomList(songsResponse.data.songs);
        }
      } catch (revertError) {
        console.error('Error reverting changes:', revertError);
      }
    }
  };

  const handleUpdateTemplate = async (templateId, templateData) => {
    try {
      await axios.put(`${API}/event-templates/${templateId}`, templateData);
      fetchTemplates();
      toast.success("Plantilla actualizada correctamente");
    } catch (error) {
      console.error("Error al actualizar plantilla:", error);
      toast.error("Error al actualizar plantilla");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) return;
    
    try {
      await axios.delete(`${API}/event-templates/${templateId}`);
      fetchTemplates();
      toast.success("Plantilla eliminada correctamente");
    } catch (error) {
      console.error("Error al eliminar plantilla:", error);
      toast.error("Error al eliminar plantilla");
    }
  };

  const fetchDenonPendientesCount = async () => {
    try {
      const response = await axios.get(`${API}/songs/stats/denon-pendientes`);
      setDenonPendientesCount(response.data.count);
    } catch (error) {
      console.error("Error al cargar conteo Denon:", error);
    }
  };

  const handleAddStyle = () => {
    if (!newStyleName.trim()) {
      toast.error("El nombre del estilo no puede estar vacío");
      return;
    }
    // Case-insensitive check for existing genres
    const newGenreLower = newStyleName.trim().toLowerCase();
    const existingGenres = getAllGenres();
    const isDuplicate = existingGenres.some(g => g.toLowerCase() === newGenreLower);
    
    if (isDuplicate) {
      toast.error("Este estilo ya existe (no distingue mayúsculas/minúsculas)");
      return;
    }
    saveCustomGenre(newStyleName.trim());
    toast.success(`Estilo "${newStyleName}" añadido correctamente`);
    setNewStyleName("");
    setIsAddStyleDialogOpen(false);
  };

  const handleAddMomento = () => {
    if (!newMomentoName.trim()) {
      toast.error("El nombre del momento no puede estar vacío");
      return;
    }
    if (getAllMomentos().includes(newMomentoName.trim())) {
      toast.error("Este momento ya existe");
      return;
    }
    saveCustomMomento(newMomentoName.trim());
    toast.success(`Momento "${newMomentoName}" añadido correctamente`);
    setNewMomentoName("");
  };

  const handleDeleteMomento = () => {
    if (!selectedMomentoToDelete) {
      toast.error("Por favor selecciona un momento");
      return;
    }
    deleteMomento(selectedMomentoToDelete);
    toast.success(`Momento "${selectedMomentoToDelete}" eliminado`);
    setSelectedMomentoToDelete("");
  };

  const handleDeleteGenre = async () => {
    if (!selectedGenreToDelete) {
      toast.error("Por favor selecciona un estilo para eliminar");
      return;
    }

    try {
      const response = await fetch(`${API}/genres/${encodeURIComponent(selectedGenreToDelete)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el estilo');
      }

      const data = await response.json();
      
      // Remove from customGenres if it exists there
      const updatedCustomGenres = customGenres.filter(g => g !== selectedGenreToDelete);
      setCustomGenres(updatedCustomGenres);
      localStorage.setItem('customGenres', JSON.stringify(updatedCustomGenres));
      
      // Add to deletedGenres list
      const updatedDeletedGenres = [...deletedGenres, selectedGenreToDelete];
      setDeletedGenres(updatedDeletedGenres);
      localStorage.setItem('deletedGenres', JSON.stringify(updatedDeletedGenres));
      
      toast.success(`Estilo "${selectedGenreToDelete}" eliminado. ${data.songs_updated} canciones actualizadas.`);
      setSelectedGenreToDelete("");
      setIsDeleteStyleDialogOpen(false);
      
      // Reload songs to reflect changes
      fetchSongs();
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al eliminar el estilo");
    }
  };

  // Spotify functions
  const handleSearchSpotify = async (songId) => {
    try {
      // axios en vez de fetch para evitar conflicto con Sentry en producción.
      const { data } = await axios.post(
        `${API}/spotify/search-song`,
        null,
        {
          params: { song_id: songId },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        }
      );

      if (data.success) {
        toast.success(`✅ Vinculado: ${data.track_name}`);
        fetchSongs();
      } else {
        toast.error(data.message || "No se encontró en Spotify");
      }
    } catch (error) {
      console.error('Error:', error);
      const detail = error?.response?.data?.detail
        || error?.message
        || "Error al buscar en Spotify";
      toast.error(detail);
    }
  };

  const handleLinkAllSpotify = async () => {
    try {
      toast.info("Vinculando todas las canciones con Spotify... esto puede tardar unos momentos");

      const { data } = await axios.post(
        `${API}/spotify/link-all-songs`,
        null,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success(`✅ Vinculadas: ${data.linked} de ${data.total_songs} canciones`);
      fetchSongs();
    } catch (error) {
      console.error('Error:', error);
      const detail = error?.response?.data?.detail
        || error?.message
        || "Error al vincular canciones con Spotify";
      toast.error(detail);
    }
  };

  const handleRelinkSongToSpotify = async (song) => {
    const confirmed = window.confirm(
      `🔄 Re-vincular con Spotify\n\n` +
      `Canción: ${song.title}\n` +
      `Artista: ${song.artist}\n\n` +
      `Esto buscará nuevamente la canción en Spotify y actualizará el enlace.\n\n` +
      `¿Continuar?`
    );

    if (!confirmed) return;

    try {
      toast.info("🔍 Buscando en Spotify...");

      // Nota: usamos axios (no fetch) porque Sentry intercepta los fetches en
      // producción y consume el body → "Body is disturbed or locked" al hacer
      // response.json(). axios no sufre ese problema.
      const { data } = await axios.post(
        `${API}/spotify/search-song`,
        null,
        {
          params: { song_id: song.id },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (data.success) {
        toast.success(`✅ Re-vinculado: ${data.track_name}`);
        fetchSongs();
      } else {
        toast.error(data.message || "No se encontró en Spotify");
      }
    } catch (error) {
      console.error('Error:', error);
      // axios pone el detail del backend en error.response.data.detail
      const detail = error?.response?.data?.detail
        || error?.message
        || "Error al re-vincular con Spotify";
      toast.error(detail);
    }
  };

  // handleSpotifySearch y handleAddFromSpotifySearch eliminados: eran código muerto
  // (nunca invocados; el diálogo <SpotifySearchDialog> tiene su propia búsqueda interna).

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    
    const songData = {
      title: formData.title.trim(),
      artist: formData.artist.trim() || "",
      genres: formData.genres || [],
      bpm: formData.bpm ? parseInt(formData.bpm) : null,
      year: formData.year ? parseInt(formData.year) : null,
      rating: formData.rating || null,
      momentos: formData.momentos || [],
      notas: formData.notas ? formData.notas.trim() : "",
      favorito: formData.favorito || false,
      pendiente_denon: formData.pendiente_denon || false,
      reproducciones: 0
    };
    
    try {
      await axios.post(`${API}/songs`, songData);
      toast.success("¡Canción añadida exitosamente!");
      setIsAddDialogOpen(false);
      resetForm();
      fetchSongs();
    } catch (error) {
      console.error("Error al añadir canción:", error);
      toast.error("Error al añadir canción");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    
    const songData = {
      title: formData.title.trim(),
      artist: formData.artist.trim() || "",
      genres: formData.genres || [],
      bpm: formData.bpm ? parseInt(formData.bpm) : null,
      year: formData.year ? parseInt(formData.year) : null,
      rating: formData.rating || null,
      momentos: formData.momentos || [],
      notas: formData.notas ? formData.notas.trim() : "",
      favorito: formData.favorito || false,
      pendiente_denon: formData.pendiente_denon || false,
      energia: formData.energia || ""
    };
    
    try {
      // Actualización optimista del cache local
      const updatedSong = { ...editingSong, ...songData };
      await offlineDB.updateSong(updatedSong);
      
      if (isOnline) {
        // Si hay conexión, actualizar en servidor usando el método del contexto
        await updateSong({ id: editingSong.id, ...songData });
        toast.success("¡Canción actualizada exitosamente!");
      } else {
        // Si no hay conexión, agregar a cola de sincronización
        await queueOfflineAction('UPDATE_SONG', { id: editingSong.id, ...songData });
        toast.warning("🔴 Cambio guardado offline - Se sincronizará cuando vuelva la conexión");
      }
      
      setIsEditDialogOpen(false);
      setEditingSong(null);
      resetForm();
      
      if (currentPlaylist) {
        fetchPlaylistSongs(currentPlaylist.id);
      }
    } catch (error) {
      console.error("Error al actualizar canción:", error);
      toast.error("Error al actualizar canción");
    }
  };

  const handleDelete = async (songId, skipConfirmation = false) => {
    logger.log('handleDelete called with songId:', songId);
    
    if (!skipConfirmation && !window.confirm("¿Estás seguro de que quieres eliminar esta canción?")) {
      logger.log('Delete cancelled by user');
      return;
    }
    
    try {
      logger.log('Sending DELETE request to:', `${API}/songs/${songId}`);
      await axios.delete(`${API}/songs/${songId}`);
      
      if (!skipConfirmation) {
        toast.success("¡Canción eliminada exitosamente!");
      }
      
      fetchSongs();
      if (currentPlaylist) {
        fetchPlaylistSongs(currentPlaylist.id);
      }
    } catch (error) {
      console.error("Error al eliminar canción:", error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al eliminar canción: ${errorMsg}`);
      throw error; // Re-throw para que el código masivo pueda manejar errores
    }
  };

  const toggleFavorite = async (song) => {
    try {
      const newFavoritoValue = !song.favorito;
      
      // Actualización optimista del cache local
      const updatedSong = { ...song, favorito: newFavoritoValue };
      await offlineDB.updateSong(updatedSong);
      
      if (isOnline) {
        // Si hay conexión, actualizar en servidor usando el método del contexto
        await updateSong({ id: song.id, favorito: newFavoritoValue });
        toast.success(song.favorito ? "Eliminado de favoritos" : "Añadido a favoritos");
      } else {
        // Si no hay conexión, agregar a cola de sincronización
        await queueOfflineAction('TOGGLE_FAVORITE', { 
          id: song.id, 
          favorito: newFavoritoValue 
        });
        toast.warning(`🔴 ${song.favorito ? "Eliminado" : "Añadido"} offline - Se sincronizará después`);
      }
    } catch (error) {
      console.error("Error al actualizar favorito:", error);
      toast.error("Error al actualizar favorito");
    }
  };

  const handleDirectRatingChange = async (song, newRating) => {
    try {
      // Actualización optimista del cache local
      const updatedSong = { ...song, rating: newRating };
      await offlineDB.updateSong(updatedSong);
      
      if (isOnline) {
        // Si hay conexión, actualizar en servidor usando el método del contexto
        await updateSong({ id: song.id, rating: newRating });
        toast.success(`Valoración actualizada: ${newRating} ${newRating === 1 ? 'estrella' : 'estrellas'}`);
      } else {
        // Si no hay conexión, agregar a cola de sincronización
        await queueOfflineAction('UPDATE_RATING', { 
          id: song.id, 
          rating: newRating 
        });
        toast.warning(`🔴 Valoración guardada offline: ${newRating} ${newRating === 1 ? 'estrella' : 'estrellas'}`);
      }
    } catch (error) {
      console.error("Error al actualizar valoración:", error);
      toast.error("Error al actualizar valoración");
    }
  };

  // Toggle favorite (alias for toggleFavorite)
  const handleToggleFavorite = toggleFavorite;

  // Toggle Denon pendiente
  const handleToggleDenon = async (song) => {
    try {
      const newValue = !song.pendiente_denon;
      
      // Actualización optimista del cache local
      const updatedSong = { ...song, pendiente_denon: newValue };
      await offlineDB.updateSong(updatedSong);
      
      if (isOnline) {
        // Si hay conexión, actualizar en servidor usando el método del contexto
        await updateSong({ id: song.id, pendiente_denon: newValue });
        toast.success(newValue ? "✅ Marcado como pendiente Denon" : "✅ Desmarcado como pendiente Denon");
      } else {
        // Si no hay conexión, agregar a cola de sincronización
        await queueOfflineAction('TOGGLE_DENON', { 
          id: song.id, 
          pendiente_denon: newValue 
        });
        toast.warning(`🔴 ${newValue ? "Marcado" : "Desmarcado"} offline - Se sincronizará después`);
      }
      
      // Refresh Denon count
      await fetchDenonPendientesCount();
    } catch (error) {
      console.error("Error al actualizar estado Denon:", error);
      toast.error("Error al actualizar estado Denon");
    }
  };

  // Handle multiple selection - open Spotify send dialog
  const handleMultipleSelection = () => {
    if (selectedSongs.length === 0) {
      toast.error("No hay canciones seleccionadas");
      return;
    }
    
    // Asegurar que selectedSongsOrdered esté actualizado antes de abrir el diálogo
    updateOrderedSelection(selectedSongs);
    
    setIsSpotifySendDialogOpen(true);
  };

  // ========== SPOTIFY QUEUE FUNCTIONS ==========
  
  // Toggle selección de canción
  const toggleSongSelection = (songId) => {
    setSelectedSongs(prev => {
      const newSelection = prev.includes(songId)
        ? prev.filter(id => id !== songId)
        : [...prev, songId];
      
      // Actualizar array ordenado
      updateOrderedSelection(newSelection);
      return newSelection;
    });
  };

  // Actualizar array ordenado basado en IDs seleccionados
  const updateOrderedSelection = (selectedIds) => {
    // Usar filteredSongs que es lo que se muestra en la biblioteca
    const ordered = filteredSongs.filter(song => selectedIds.includes(song.id));
    setSelectedSongsOrdered(ordered);
  };

  // Seleccionar todas las canciones visibles
  // selectAllSongs eliminado: código muerto (sin invocaciones en el codebase).

  // Deseleccionar todas
  const deselectAllSongs = () => {
    setSelectedSongs([]);
    setSelectedSongsOrdered([]);
  };

  // Reordenar canciones seleccionadas (drag & drop)
  // reorderSelectedSongs eliminado: código muerto (sin invocaciones en el codebase).

  // Eliminar canción de la selección
  // removeFromSelection eliminado: código muerto (sin invocaciones en el codebase).

  // Enviar canciones seleccionadas a Spotify
  const sendToSpotify = async () => {
    if (!spotifyConnected) {
      toast.error('Por favor conecta con Spotify primero');
      return;
    }

    if (selectedSongsOrdered.length === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }

    try {
      // Obtener URIs de Spotify en el orden correcto
      const trackUris = selectedSongsOrdered
        .filter(song => song.spotify_uri)
        .map(song => song.spotify_uri);

      if (trackUris.length === 0) {
        toast.error('Ninguna canción seleccionada tiene enlace de Spotify');
        return;
      }

      // Aplicar opciones de shuffle/repeat si están activadas
      if (spotifySendOptions.shuffle) {
        await toggleShuffle(true);
      }
      
      if (spotifySendOptions.repeat !== 'off') {
        await setRepeatMode(spotifySendOptions.repeat);
      }

      // Enviar a cola de Spotify
      await addMultipleToQueue(trackUris);

      // Limpiar selección y cerrar dialog
      setSelectedSongs([]);
      setSelectedSongsOrdered([]);
      setIsSpotifySendDialogOpen(false);
      setSpotifySendOptions({ shuffle: false, repeat: 'off' });

    } catch (error) {
      console.error('Error sending to Spotify:', error);
      toast.error('Error al enviar a Spotify');
    }
  };

  // sendAIRecsToSpotify removed (Ola cleanup 2026-02): entire function was orphan — never invoked anywhere.

  // Enviar canciones de un momento específico a Spotify
  const sendMomentoToSpotify = async (momento, withShuffle = false) => {
    if (!spotifyConnected) {
      toast.error('Por favor conecta con Spotify primero');
      return;
    }

    if (!currentPlaylist || !currentPlaylist.lista_personalizada_generada) {
      toast.error('No hay lista personalizada');
      return;
    }

    try {
      // Obtener canciones del momento
      const playlistSongs = currentPlaylist.lista_personalizada_generada
        .map(songId => songs.find(s => s.id === songId))
        .filter(song => song && song.momentos && song.momentos.includes(momento));

      if (playlistSongs.length === 0) {
        toast.error(`No hay canciones para el momento "${momento}"`);
        return;
      }

      // Obtener URIs
      const trackUris = playlistSongs
        .filter(song => song.spotify_uri)
        .map(song => song.spotify_uri);

      if (trackUris.length === 0) {
        toast.error('Las canciones no tienen enlace de Spotify');
        return;
      }

      // Aplicar shuffle si se solicita
      if (withShuffle) {
        await toggleShuffle(true);
      } else {
        await toggleShuffle(false);
      }

      // Enviar a cola
      await addMultipleToQueue(trackUris);

      toast.success(`${trackUris.length} canciones de "${momento}" enviadas a Spotify`);

    } catch (error) {
      console.error('Error sending momento to Spotify:', error);
      toast.error('Error al enviar momento a Spotify');
    }
  };

  const handleExportPDF = async (playlistId) => {
    try {
      const response = await axios.post(
        `${API}/playlists/${playlistId}/export-custom-list-pdf`,
        {},
        { responseType: 'blob' }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `playlist_${currentPlaylist.nombre_novios.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      console.error("Error al exportar PDF:", error);
      toast.error("Error al exportar PDF");
    }
  };

  const handleCreatePlaylist = async (playlistData) => {
    try {
      const response = await axios.post(`${API}/playlists`, playlistData);
      toast.success("¡Playlist creada exitosamente!");
      setIsCreatePlaylistOpen(false);
      fetchPlaylists();
      // Ir directo al detalle del evento recién creado
      if (response.data?.id) {
        openPlaylistDetail(response.data);
      }
    } catch (error) {
      console.error("Error al crear playlist:", error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al crear playlist: ${errorMsg}`);
    }
  };

  const handleEditPlaylist = async (playlistData) => {
    if (!editingPlaylist) return;
    
    try {
      await axios.put(`${API}/playlists/${editingPlaylist.id}`, playlistData);
      toast.success("¡Playlist actualizada exitosamente!");
      setIsEditPlaylistOpen(false);
      setEditingPlaylist(null);
      fetchPlaylists();
      if (currentPlaylist && currentPlaylist.id === editingPlaylist.id) {
        // Refresh current playlist
        const response = await axios.get(`${API}/playlists/${editingPlaylist.id}`);
        setCurrentPlaylist(response.data);
      }
    } catch (error) {
      console.error("Error al actualizar playlist:", error);
      toast.error("Error al actualizar playlist");
    }
  };

  const openEditPlaylist = (playlist) => {
    setEditingPlaylist(playlist);
    setIsEditPlaylistOpen(true);
  };

  const handleCalendarEventSelect = async (playlistId) => {
    try {
      const response = await axios.get(`${API}/playlists/${playlistId}`);
      openPlaylistDetail(response.data);
    } catch (error) {
      console.error('Error loading playlist:', error);
      toast.error('Error al cargar el evento');
    }
  };

  // handleExportContract removed (Ola D cleanup 2026-02): UI trigger deleted Dec 2025 (commit e7da893) when flow migrated to GestionContratos.jsx.

  // Función para verificar si una canción coincide con un término de búsqueda
  const matchesSong = (song, searchTerm) => {
    const normalizedSearch = normalizeText(searchTerm);
    
    // Buscar en título
    if (normalizeText(song.title || '').includes(normalizedSearch)) return true;
    
    // Buscar en artista
    if (normalizeText(song.artist || '').includes(normalizedSearch)) return true;
    
    // Buscar en géneros
    if (song.genres && song.genres.some(genre => normalizeText(genre).includes(normalizedSearch))) return true;
    
    // Buscar en momentos
    if (song.momentos && song.momentos.some(momento => normalizeText(momento).includes(normalizedSearch))) return true;
    
    // Buscar en energía
    if (song.energia && normalizeText(song.energia).includes(normalizedSearch)) return true;
    
    // Buscar en BPM
    if (song.bpm && song.bpm.toString().includes(searchTerm)) return true;
    
    return false;
  };

  // Función para verificar si contiene texto
  const containsText = (text, search) => {
    const normalizedText = normalizeText(text);
    const normalizedSearch = normalizeText(search);
    return normalizedText.includes(normalizedSearch);
  };

  // Función para verificar si una canción coincide con búsqueda
  const matchesSongSearch = (song, searchTerm) => {
    if (!searchTerm) return true;
    
    // Buscar en título
    if (containsText(song.title || '', searchTerm)) return true;
    
    // Buscar en artista
    if (containsText(song.artist || '', searchTerm)) return true;
    
    // Buscar en géneros
    if (song.genres && song.genres.some(genre => containsText(genre, searchTerm))) return true;
    
    // Buscar en momentos
    if (song.momentos && song.momentos.some(momento => containsText(momento, searchTerm))) return true;
    
    // Buscar en BPM (convertir a string)
    if (song.bpm && song.bpm.toString().includes(searchTerm)) return true;
    
    return false;
  };

  // Función para filtrar lista personalizada por búsqueda
  // handleCustomListSearch eliminado: código muerto (sin invocaciones en el codebase).

  // handleGenerateCustomList eliminado: código muerto (sin invocaciones en el codebase).

  const handleExportCustomListPDF = async (playlistId) => {
    try {
      const playlist = playlistId ? playlists.find(p => p.id === playlistId) : currentPlaylist;
      
      if (!playlist || !playlist.lista_personalizada_generada || playlist.lista_personalizada_generada.length === 0) {
        toast.error('No hay lista personalizada para exportar');
        return;
      }

      // Obtener datos completos de las canciones
      const songsData = playlist.lista_personalizada_generada
        .map(songId => songs.find(s => s.id === songId))
        .filter(s => s);

      const response = await axios.post(`${API}/playlists/${playlist.id}/export-custom-list-pdf`, {
        songs: songsData
      }, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lista_personalizada_${playlist.nombre_novios.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Lista personalizada exportada exitosamente");
    } catch (error) {
      console.error("Error al exportar lista personalizada:", error);
      toast.error("Error al exportar lista personalizada");
    }
  };

  const handleExportToEngineDJ = async (playlistId) => {
    try {
      const playlist = playlistId ? playlists.find(p => p.id === playlistId) : currentPlaylist;
      
      logger.log('[Export Engine DJ] Playlist:', playlist);
      logger.log('[Export Engine DJ] lista_personalizada_generada:', playlist?.lista_personalizada_generada);
      
      if (!playlist) {
        toast.error('No se encontró el evento');
        return;
      }
      
      if (!playlist.lista_personalizada_generada || playlist.lista_personalizada_generada.length === 0) {
        toast.error('Debes generar primero una lista personalizada para este evento');
        return;
      }

      toast.info('Exportando a Engine DJ...');

      const response = await axios.post(`${API}/playlists/${playlist.id}/export-engine-dj`, {}, {
        responseType: 'blob'
      });
      
      logger.log('[Export Engine DJ] Response received, size:', response.data.size);
      
      // Create blob link to download
      const blob = new Blob([response.data], { type: 'audio/x-mpegurl' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = playlist.nombre_novios ? `engine_dj_${playlist.nombre_novios.replace(/\s+/g, '_')}.m3u8` : 'engine_dj_playlist.m3u8';
      link.setAttribute('download', fileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      logger.log('[Export Engine DJ] Triggering download:', fileName);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("🎧 Lista exportada para Engine DJ exitosamente");
    } catch (error) {
      console.error("Error al exportar a Engine DJ:", error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al exportar a Engine DJ: ${errorMsg}`);
    }
  };

  // handleExportCustomListExcel eliminado: código muerto (sin invocaciones en el codebase).

  // handleGenerateAIRecommendations eliminado: código muerto (sin invocaciones en el codebase).

  const handleAddAIRecommendationToLibrary = async (recommendation) => {
    try {
      // Validar que tenemos título y artista
      if (!recommendation.title || !recommendation.artist) {
        toast.error('La recomendación no tiene título o artista');
        return;
      }

      let spotifyUrl = '';
      
      // Intentar buscar en Spotify (sin bloquear si falla)
      try {
        const spotifyResponse = await axios.get(`${API}/songs/search-spotify`, {
          params: {
            query: `${recommendation.title} ${recommendation.artist}`
          }
        });

        if (spotifyResponse.data.tracks && spotifyResponse.data.tracks.length > 0) {
          spotifyUrl = spotifyResponse.data.tracks[0].external_urls?.spotify || '';
        }
      } catch (spotifyError) {
        console.warn('No se pudo buscar en Spotify:', spotifyError);
        // Continuar sin Spotify URL
      }

      // Crear la canción en la biblioteca
      const songData = {
        title: recommendation.title,
        artist: recommendation.artist,
        genres: Array.isArray(recommendation.genres) ? recommendation.genres : [],
        bpm: recommendation.estimated_bpm || null,
        rating: null,
        momentos: [],
        notas: `Recomendada por IA: ${recommendation.reason || 'Sugerencia inteligente'}`,
        favorito: false,
        reproducciones: 0,
        spotify_url: spotifyUrl
      };

      await axios.post(`${API}/songs`, songData);
      toast.success(`✅ "${recommendation.title}" añadida a la biblioteca`);
      fetchSongs();
    } catch (error) {
      console.error('Error adding AI recommendation to library:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al añadir canción: ${errorMsg}`);
    }
  };

  // handleGenerateCustomListForEvent eliminado: código muerto (sin invocaciones en el codebase).

  const handleRemoveSongFromCustomList = async (songId) => {
    try {
      // Eliminar canción de la lista personalizada del evento actual
      const updatedList = currentPlaylist.lista_personalizada_generada.filter(id => id !== songId);
      
      // Optimistic update - actualizar UI inmediatamente
      const updatedPlaylist = {
        ...currentPlaylist,
        lista_personalizada_generada: updatedList
      };
      setCurrentPlaylist(updatedPlaylist);
      
      // Actualizar también generatedCustomList si existe
      if (generatedCustomList && generatedCustomList.length > 0) {
        const updatedGeneratedList = generatedCustomList.filter(song => song.id !== songId);
        setGeneratedCustomList(updatedGeneratedList);
        setFilteredCustomList(updatedGeneratedList);
      }
      
      toast.success('✅ Canción eliminada');
      
      // Actualizar en el backend (en segundo plano, sin bloquear).
      // Solo el campo que cambia: enviar el evento completo pisaba `peticiones` ajenas.
      axios.put(`${API}/playlists/${currentPlaylist.id}`, {
        lista_personalizada_generada: updatedList
      }).then(() => {
        // Sincronizar con el contexto en segundo plano
        refetchPlaylists();
      }).catch(error => {
        console.error('Error syncing with backend:', error);
      });
      
    } catch (error) {
      console.error('Error removing song from custom list:', error);
      toast.error('Error al eliminar canción de la lista');
      
      // Revertir cambios si falla
      setCurrentPlaylist(currentPlaylist);
      if (generatedCustomList) {
        setGeneratedCustomList(generatedCustomList);
        setFilteredCustomList(generatedCustomList);
      }
    }
  };

  // Handlers de peticiones (remove, toggle-hidden) extraídos a `usePeticionesHandlers` arriba.


  
  // Handler para reordenar peticiones con drag-and-drop
  const handleAddSongToCustomList = async (songId) => {
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('Abre un evento primero');
      return;
    }
    
    // Verificar si la canción ya está en la lista
    if (currentPlaylist.lista_personalizada_generada?.includes(songId)) {
      toast.info('La canción ya está en la lista');
      return;
    }
    
    // Buscar la canción en la biblioteca (paginada) con fallback al cache
    // de useAllSongs y finalmente al endpoint /api/songs/{id}.
    // Fix feb/2026: antes sólo miraba `songs` (20 items paginados), lo que
    // provocaba "Canción no encontrada" al añadir desde el dialog que carga
    // la biblioteca completa (8000+ canciones en prod).
    let songToAdd = songs.find(s => s.id === songId);
    if (!songToAdd) {
      const cachedAll = queryClient.getQueryData(['songs', 'all']);
      if (Array.isArray(cachedAll)) {
        songToAdd = cachedAll.find(s => s.id === songId);
      }
    }
    if (!songToAdd) {
      try {
        const { data } = await axios.get(`${API}/songs/${songId}`);
        songToAdd = data;
      } catch (err) {
        console.error('[ERROR] Fetching song by id failed:', err);
      }
    }
    if (!songToAdd) {
      toast.error('Canción no encontrada');
      return;
    }
    
    try {
      // Optimistic update - actualizar UI inmediatamente
      const updatedIdsList = [...(currentPlaylist.lista_personalizada_generada || []), songId];
      const updatedPlaylist = {
        ...currentPlaylist,
        lista_personalizada_generada: updatedIdsList
      };
      setCurrentPlaylist(updatedPlaylist);
      
      // IMPORTANTE: Actualizar también filteredCustomList y generatedCustomList
      // para que la UI muestre la nueva canción inmediatamente
      setFilteredCustomList(prev => [...prev, songToAdd]);
      setGeneratedCustomList(prev => [...prev, songToAdd]);
      
      // Llamada al backend en segundo plano (sin await)
      axios.post(`${API}/playlists/${currentPlaylist.id}/add-song-to-custom-list?song_id=${songId}`)
        .then(response => {
          if (response.data.added) {
            toast.success('✅ Canción añadida');
            // Sincronizar con el contexto en segundo plano
            refetchPlaylists();
          } else {
            // Si ya existía, revertir el cambio local
            setCurrentPlaylist(currentPlaylist);
            setFilteredCustomList(prev => prev.filter(s => s.id !== songId));
            setGeneratedCustomList(prev => prev.filter(s => s.id !== songId));
            toast.info('La canción ya está en la lista');
          }
        })
        .catch(error => {
          // Revertir cambio local en caso de error
          setCurrentPlaylist(currentPlaylist);
          setFilteredCustomList(prev => prev.filter(s => s.id !== songId));
          setGeneratedCustomList(prev => prev.filter(s => s.id !== songId));
          console.error('[ERROR] Error adding song to custom list:', error);
          toast.error('Error al añadir canción');
        });
        
    } catch (error) {
      console.error('[ERROR] Error adding song to custom list:', error);
      toast.error('Error al añadir canción');
    }
  };

  // Insertar o mover una canción JUSTO DESPUÉS de otra canción en la lista
  const handleInsertSongAfter = async (songIdToInsert, afterSongId) => {
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('Abre un evento primero');
      return;
    }
    
    const currentList = currentPlaylist.lista_personalizada_generada || [];
    const afterIndex = currentList.indexOf(afterSongId);
    
    if (afterIndex === -1) {
      // La canción base no está en la lista, añadir al final
      await handleAddSongToCustomList(songIdToInsert);
      return;
    }
    
    // Buscar la canción a insertar en la biblioteca (con fallback a cache + API)
    let songToInsert = songs.find(s => s.id === songIdToInsert);
    if (!songToInsert) {
      const cachedAll = queryClient.getQueryData(['songs', 'all']);
      if (Array.isArray(cachedAll)) {
        songToInsert = cachedAll.find(s => s.id === songIdToInsert);
      }
    }
    if (!songToInsert) {
      try {
        const { data } = await axios.get(`${API}/songs/${songIdToInsert}`);
        songToInsert = data;
      } catch (err) {
        console.error('[ERROR] Fetching song by id failed:', err);
      }
    }
    if (!songToInsert) {
      toast.error('Canción no encontrada');
      return;
    }
    
    try {
      let newList;
      const existingIndex = currentList.indexOf(songIdToInsert);
      
      if (existingIndex !== -1) {
        // La canción ya existe en la lista - MOVERLA
        newList = currentList.filter(id => id !== songIdToInsert);
        const newAfterIndex = newList.indexOf(afterSongId);
        newList.splice(newAfterIndex + 1, 0, songIdToInsert);
        toast.success('🔄 Canción movida a la posición correcta');
      } else {
        // La canción no existe - INSERTARLA después de afterSongId
        newList = [...currentList];
        newList.splice(afterIndex + 1, 0, songIdToInsert);
        toast.success('✅ Canción insertada después de la actual');
      }
      
      // Actualizar UI inmediatamente
      const updatedPlaylist = {
        ...currentPlaylist,
        lista_personalizada_generada: newList
      };
      setCurrentPlaylist(updatedPlaylist);
      
      // Reconstruir la lista de canciones en el orden correcto
      const newSongsList = newList.map(id => {
        const existing = generatedCustomList.find(s => s.id === id);
        if (existing) return existing;
        return songs.find(s => s.id === id);
      }).filter(Boolean);
      
      setGeneratedCustomList(newSongsList);
      setFilteredCustomList(newSongsList);
      
      // Guardar en backend
      await axios.put(`${API}/playlists/${currentPlaylist.id}`, {
        lista_personalizada_generada: newList
      });
      
      refetchPlaylists();
      
    } catch (error) {
      console.error('[ERROR] Error inserting song:', error);
      toast.error('Error al insertar canción');
      // Refetch para sincronizar estado
      if (currentPlaylist?.id) {
        fetchPlaylistSongs(currentPlaylist.id);
      }
    }
  };

  // Fetch playlist templates
  const fetchPlaylistTemplates = async () => {
    try {
      const response = await axios.get(`${API}/playlist-templates`);
      setPlaylistTemplates(response.data);
    } catch (error) {
      console.error('Error fetching playlist templates:', error);
    }
  };

  // Save current custom list as template
  const handleSaveAsTemplate = async (e) => {
    e.preventDefault();
    
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('No hay evento seleccionado');
      return;
    }
    
    if (!currentPlaylist.lista_personalizada_generada || currentPlaylist.lista_personalizada_generada.length === 0) {
      toast.error('La lista personalizada está vacía');
      return;
    }
    
    if (!templateForm.nombre.trim()) {
      toast.error('El nombre de la plantilla es obligatorio');
      return;
    }
    
    try {
      // Unificado con el flujo del Analyzer (iter 2026-02): usamos el endpoint
      // JSON con body para evitar Query params con comas en `tags` y mantener
      // un solo camino al sistema de Plantillas.
      const tagsArr = String(templateForm.tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const songIds = Array.isArray(currentPlaylist.lista_personalizada_generada)
        ? currentPlaylist.lista_personalizada_generada
        : [];
      const response = await axios.post(
        `${API}/playlist-templates/json`,
        {
          nombre: templateForm.nombre,
          descripcion: templateForm.descripcion || '',
          song_ids: songIds,
          tipo_evento: templateForm.tipo_evento || '',
          tags: tagsArr,
        }
      );

      toast.success(`Plantilla "${templateForm.nombre}" guardada con ${response.data.song_ids.length} canciones`);
      setIsSaveTemplateOpen(false);
      setTemplateForm({ nombre: '', descripcion: '', tipo_evento: '', tags: '' });
      
      // Refresh templates
      await fetchPlaylistTemplates();
      
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  // Apply template to current playlist. Si el evento ya tiene canciones,
  // pregunta si reemplazar o combinar (con dedup inteligente por texto en backend).
  const handleApplyTemplate = async (templateId) => {
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('No hay evento seleccionado');
      return;
    }

    const existingCount = (currentPlaylist.lista_personalizada_generada || []).length;
    let mode = 'replace';
    if (existingCount > 0) {
      // window.confirm sirve aquí como diálogo simple 2-opciones. "OK"=merge,
      // "Cancelar"=replace para no perder el flujo rápido.
      const merge = window.confirm(
        `Este evento ya tiene ${existingCount} canción${existingCount !== 1 ? 'es' : ''}.\n\n` +
        `OK  = Combinar (añade sin duplicar, incluso variaciones de título/artista)\n` +
        `Cancelar = Reemplazar (sobrescribe la lista actual)`
      );
      mode = merge ? 'merge' : 'replace';
    }

    try {
      const response = await axios.post(
        `${API}/playlists/${currentPlaylist.id}/apply-template/${templateId}?mode=${mode}`
      );

      toast.success(response.data.message + ` · total ${response.data.songs_count}`);

      const updatedPlaylist = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
      setCurrentPlaylist(updatedPlaylist.data);

      // Refrescar la lista hidratada (bug fix abr/2026): antes solo
      // actualizábamos los IDs en currentPlaylist y la UI seguía mostrando
      // el `generatedCustomList` viejo (o vacío) → contador "N canciones"
      // pero lista vacía. Ahora rehidratamos desde /songs/by-ids.
      const newIds = updatedPlaylist.data?.lista_personalizada_generada || [];
      if (newIds.length > 0) {
        try {
          const songsResponse = await axios.post(`${API}/songs/by-ids`, { ids: newIds });
          setGeneratedCustomList(songsResponse.data.songs || []);
          setFilteredCustomList(songsResponse.data.songs || []);
        } catch (fetchErr) {
          console.error('Error hydrating custom list after template apply:', fetchErr);
        }
      } else {
        setGeneratedCustomList([]);
        setFilteredCustomList([]);
      }

      setIsApplyTemplateOpen(false);

    } catch (error) {
      console.error('Error applying template:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar plantilla');
    }
  };

  // Apply Smart Playlist to current event
  const handleApplySmartPlaylist = async (smartPlaylist) => {
    if (!currentPlaylist || !currentPlaylist.id) {
      toast.error('No hay evento seleccionado');
      return;
    }
    
    if (!smartPlaylist.songs || smartPlaylist.songs.length === 0) {
      toast.error('Esta playlist no tiene canciones');
      return;
    }
    
    try {
      // Extract song IDs from the smart playlist
      const songIds = smartPlaylist.songs.map(song => song.id);
      
      // Update the current event's lista_personalizada_generada
      const response = await axios.put(`${API}/playlists/${currentPlaylist.id}`, {
        lista_personalizada_generada: songIds
      });
      
      toast.success(`Playlist inteligente aplicada: ${smartPlaylist.name} (${songIds.length} canciones)`);
      
      // Refresh playlist data
      const updatedPlaylist = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
      setCurrentPlaylist(updatedPlaylist.data);
      setGeneratedCustomList(smartPlaylist.songs);
      
      setIsApplyTemplateOpen(false);
      
    } catch (error) {
      console.error('Error applying smart playlist:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar playlist inteligente');
    }
  };

  // Delete playlist template
  const handleDeletePlaylistTemplate = async (templateId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/playlist-templates/${templateId}`);
      toast.success('Plantilla eliminada');
      await fetchPlaylistTemplates();
    } catch (error) {
      console.error('Error deleting playlist template:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar plantilla');
    }
  };

  const handleExportTemplateToEngineDJ = async (templateId, templateName) => {
    try {
      toast.info('Exportando plantilla a Engine DJ...');

      const response = await axios.post(`${API}/playlist-templates/${templateId}/export-engine-dj`, {}, {
        responseType: 'blob'
      });
      
      logger.log('[Export Template Engine DJ] Response received, size:', response.data.size);
      
      // Create blob link to download
      const blob = new Blob([response.data], { type: 'audio/x-mpegurl' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `engine_dj_${templateName.replace(/\s+/g, '_')}.m3u8`);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      logger.log('[Export Template Engine DJ] Triggering download:', templateName);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("🎧 Plantilla exportada para Engine DJ exitosamente");
    } catch (error) {
      console.error("Error al exportar plantilla a Engine DJ:", error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al exportar plantilla a Engine DJ: ${errorMsg}`);
    }
  };

  const handleGenerateAIRecommendationsForEvent = async (playlistId) => {
    logger.log('handleGenerateAIRecommendationsForEvent called with:', playlistId);
    setIsGeneratingRecommendations(true);
    
    try {
      logger.log('Sending request to Master DJ for recommendations...');
      // Usar el nuevo endpoint del Master DJ
      const response = await runAiJob({
        startUrl: `${API}/dj-assistant/playlist-recommendations/start`,
        statusBase: `${API}/dj-assistant/jobs`,
        body: {
          playlist_id: playlistId,
          num_recommendations: 15
        },
      });
      logger.log('Master DJ recommendations response:', response.data);

      // Actualizar estado local
      const playlist = playlists.find(p => p.id === playlistId);
      const updatedPlaylist = {
        ...playlist,
        ai_recommendations: response.data.recommendations
      };
      setCurrentPlaylist(updatedPlaylist);
      
      // Refetch playlists para sincronizar con el backend
      await refetchPlaylists();

      const source = response.data.used_memories ? '(con experiencias del DJ)' : '';
      toast.success(`✨ ${response.data.recommendations.length} recomendaciones de Master DJ ${source}`);
    } catch (error) {
      console.error('Error generating Master DJ recommendations for event:', error);
      console.error('Error details:', error.response?.data);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('La generación de Master DJ tardó demasiado. Intenta con una lista más corta.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
        toast.error(`Error al generar recomendaciones: ${errorMsg}`);
      }
    } finally {
      setIsGeneratingRecommendations(false);
      logger.log('handleGenerateAIRecommendationsForEvent finished');
    }
  };

  // handleAISearch removed - functionality moved to Master DJ Assistant

  // handleAddAIResultToLibrary eliminado: código muerto (sin invocaciones en el codebase).

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor, selecciona un archivo CSV válido');
      return;
    }

    toast.info('Iniciando importación...', { duration: 2000 });

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('El archivo CSV está vacío o no tiene datos');
        return;
      }

      // Obtener géneros disponibles en la app
      const normalizedAvailableGenres = getAllGenres().map(g => ({
        original: g,
        normalized: normalizeText(g)
      }));

      // Función para encontrar género similar
      const findSimilarGenre = (csvGenre) => {
        if (!csvGenre) return null;
        
        const normalized = normalizeText(csvGenre);
        
        // Buscar coincidencia exacta
        const exact = normalizedAvailableGenres.find(g => g.normalized === normalized);
        if (exact) return exact.original;
        
        // Buscar si contiene o está contenido (con flexibilidad)
        const partial = normalizedAvailableGenres.find(g => 
          g.normalized.includes(normalized) || normalized.includes(g.normalized)
        );
        if (partial) return partial.original;
        
        // No encontrado - devolver null
        return null;
      };

      // Parsear header con normalización
      const headerLine = lines[0];
      const headers = headerLine.split(',').map(h => h.trim().replace(/['"]/g, ''));
      const normalizedHeaders = headers.map(h => normalizeText(h));
      
      // Buscar columnas con diferentes variaciones (sin tildes)
      const findColumnIndex = (variations) => {
        const normalizedVariations = variations.map(v => normalizeText(v));
        return normalizedHeaders.findIndex(h => 
          normalizedVariations.some(v => h.includes(v))
        );
      };
      
      const titleIndex = findColumnIndex(['title', 'titulo', 'cancion', 'canción', 'song', 'tema']);
      const artistIndex = findColumnIndex(['artist', 'artista', 'autor', 'interprete', 'intérprete']);
      const genreIndex = findColumnIndex(['genre', 'genero', 'género', 'generos', 'géneros', 'style', 'estilo']);
      const bpmIndex = findColumnIndex(['bpm', 'tempo']);
      const ratingIndex = findColumnIndex(['rating', 'valoracion', 'valoración', 'puntuacion', 'puntuación', 'estrellas']);
      
      if (titleIndex === -1) {
        toast.error('El CSV debe tener una columna con el título de la canción (title, titulo, cancion, etc.)');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;

      // Procesar cada línea
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        try {
          // Parsear CSV respetando comillas
          const values = [];
          let currentValue = '';
          let inQuotes = false;
          
          for (let char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim()); // Añadir último valor

          // Extraer solo los campos reconocidos, ignorar el resto
          const title = titleIndex !== -1 && values[titleIndex] ? values[titleIndex].replace(/^"|"$/g, '').trim() : '';
          
          // Título es obligatorio
          if (!title) {
            skippedCount++;
            continue;
          }

          // Artista es obligatorio (si no está en CSV, intentar obtenerlo de Spotify)
          let artist = artistIndex !== -1 && values[artistIndex] ? values[artistIndex].replace(/^"|"$/g, '').trim() : '';
          
          // Obtener otros campos opcionales (ignorar si no existen)
          const genreValue = genreIndex !== -1 && values[genreIndex] ? values[genreIndex].replace(/^"|"$/g, '').trim() : '';
          const csvGenres = genreValue ? genreValue.split(/[;|\/]/).map(g => g.trim()).filter(g => g) : [];
          
          // Mapear géneros del CSV a géneros disponibles en la app
          const genres = csvGenres.map(csvGenre => findSimilarGenre(csvGenre)).filter(g => g !== null);
          
          const bpmValue = bpmIndex !== -1 && values[bpmIndex] ? values[bpmIndex].replace(/^"|"$/g, '').trim() : '';
          const bpm = bpmValue && !isNaN(parseInt(bpmValue)) ? parseInt(bpmValue) : null;
          
          const ratingValue = ratingIndex !== -1 && values[ratingIndex] ? values[ratingIndex].replace(/^"|"$/g, '').trim() : '';
          const rating = ratingValue && !isNaN(parseInt(ratingValue)) ? Math.min(5, Math.max(1, parseInt(ratingValue))) : null;

          // Buscar en Spotify si tenemos título
          let spotifyUrl = '';
          
          try {
            const spotifyResponse = await axios.get(`${API}/songs/search-spotify`, {
              params: { query: `${title} ${artist || ''}`.trim() }
            });

            if (spotifyResponse.data.tracks && spotifyResponse.data.tracks.length > 0) {
              const track = spotifyResponse.data.tracks[0];
              spotifyUrl = track.external_urls?.spotify || '';
              
              // Si no teníamos artista, usar el de Spotify
              if (!artist && track.artists && track.artists.length > 0) {
                artist = track.artists[0].name;
              }
            }
          } catch (spotifyError) {
            console.warn(`No se encontró en Spotify: ${title}`, spotifyError);
          }

          // Artista sigue siendo obligatorio después de intentar Spotify
          if (!artist) {
            console.warn(`Canción sin artista omitida: ${title}`);
            skippedCount++;
            continue;
          }

          // Crear canción con todos los datos disponibles
          const songData = {
            title: title,
            artist: artist,
            genres: genres,
            bpm: bpm,
            rating: rating,
            momentos: [],
            notas: 'Importado desde CSV',
            favorito: false,
            reproducciones: 0,
            spotify_url: spotifyUrl
          };

          await axios.post(`${API}/songs`, songData);
          successCount++;
          
          // Mostrar progreso cada 5 canciones
          if (successCount % 5 === 0) {
            toast.info(`Procesando... ${successCount} canciones añadidas`, { duration: 1000 });
          }
          
          // Pequeña pausa para no saturar la API
          if (i % 5 === 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (error) {
          console.error(`Error en línea ${i}:`, error);
          errorCount++;
        }
      }

      // Mensaje final
      if (successCount > 0) {
        toast.success(`✅ Importación completada: ${successCount} canciones añadidas${skippedCount > 0 ? `, ${skippedCount} omitidas` : ''}${errorCount > 0 ? `, ${errorCount} errores` : ''}`);
        fetchSongs();
      } else {
        toast.error(`❌ No se pudo importar ninguna canción. ${skippedCount} líneas omitidas (falta título o artista), ${errorCount} errores`);
      }
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(`Error al procesar el archivo CSV: ${error.message}`);
      event.target.value = '';
    }
  };

  const handleAddSongToPlaylist = async (playlistId, songId) => {
    try {
      await axios.post(`${API}/playlists/${playlistId}/songs`, { song_id: songId });
      toast.success("Canción añadida a la playlist");
      if (currentPlaylist && currentPlaylist.id === playlistId) {
        fetchPlaylistSongs(playlistId);
      }
      setIsAddToPlaylistOpen(false);
    } catch (error) {
      console.error("Error al añadir canción:", error);
      toast.error(error.response?.data?.detail || "Error al añadir canción");
    }
  };

  const handleRemoveSongFromPlaylist = async (songId) => {
    if (!window.confirm("¿Quitar esta canción de la playlist?")) {
      return;
    }

    try {
      await axios.delete(`${API}/playlists/${currentPlaylist.id}/songs/${songId}`);
      toast.success("Canción eliminada de la playlist");
      fetchPlaylistSongs(currentPlaylist.id);
    } catch (error) {
      console.error("Error al eliminar canción:", error);
      toast.error("Error al eliminar canción");
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta playlist?")) {
      return;
    }

    try {
      await axios.delete(`${API}/playlists/${playlistId}`);
      toast.success("Playlist eliminada");
      fetchPlaylists();
      if (currentPlaylist && currentPlaylist.id === playlistId) {
        setView("playlists");
        setCurrentPlaylist(null);
        // Limpiar la URL deep-link del evento eliminado
        if (location.search.includes('view=playlist-detail')) {
          navigate('/', { replace: true });
        }
      }
    } catch (error) {
      console.error("Error al eliminar playlist:", error);
      toast.error("Error al eliminar playlist");
    }
  };

  const openEditDialog = (song) => {
    setEditingSong(song);
    setFormData({
      title: song.title,
      artist: song.artist || "",
      genres: song.genres || [],
      bpm: song.bpm ? song.bpm.toString() : "",
      year: song.year ? song.year.toString() : "",
      rating: song.rating || 0,
      energia: song.energia || "",
      momentos: song.momentos || [],
      notas: song.notas || "",
      favorito: song.favorito || false,
      pendiente_denon: song.pendiente_denon || false,
      spotify_url: song.spotify_url || "",
      spotify_uri: song.spotify_uri || ""
    });
    setIsEditDialogOpen(true);
  };

  // Long-press en el título de la canción dentro de la playlist personalizada
  // → abre el diálogo de edición. Muestra hint en el primer uso.
  const handleLongPressEditSong = (song) => {
    if (!song) return;
    try {
      const seenHint = localStorage.getItem('long_press_edit_hint_seen');
      if (!seenHint) {
        toast.info('💡 Mantén pulsado 0,6s sobre el título para editar', { duration: 3000 });
        localStorage.setItem('long_press_edit_hint_seen', '1');
      }
    } catch {
      /* localStorage no disponible — no es crítico */
    }
    openEditDialog(song);
  };

  const handleDeletePastEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      // Using query parameter on existing route to avoid proxy routing issues
      const response = await axios.get(`${API}/playlists?action=clean-past-mp3s`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        refetchPlaylists();
      }
    } catch (error) {
      console.error('Error cleaning past events MP3s:', error);
      toast.error('Error al limpiar MP3s de eventos pasados: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAllEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/playlists/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`🗑️ ${response.data.message}`);
        refetchPlaylists();
      }
    } catch (error) {
      console.error('Error deleting all events:', error);
      toast.error('Error al eliminar todos los eventos: ' + (error.response?.data?.detail || error.message));
    }
  };

  const generateClientAccessCode = async (playlist) => {
    // Validar que el evento tiene ID
    if (!playlist || !playlist.id) {
      toast.error('Este evento no tiene ID. Por favor, edita el evento primero.');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/playlists/client/generate-code/${playlist.id}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.codigo_acceso) {
        const code = response.data.codigo_acceso;
        const alreadyExists = response.data.already_exists;
        
        // Prepare portal URL with code and message
        const portalUrl = `${window.location.origin}/portal?code=${code}`;
        const message = `🎉 Portal de Cliente - ${playlist.nombre_novios || 'Evento'}\n\nAccede directamente aquí:\n${portalUrl}`;
        
        // Try to copy to clipboard with fallback
        let copied = false;
        try {
          // Modern clipboard API (preferred)
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(message);
            copied = true;
          } else {
            // Fallback for older browsers or non-secure contexts
            const textArea = document.createElement('textarea');
            textArea.value = message;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
              document.execCommand('copy');
              copied = true;
            } catch (err) {
              console.error('Fallback copy failed:', err);
            }
            
            document.body.removeChild(textArea);
          }
        } catch (clipboardError) {
          console.error('Clipboard error:', clipboardError);
        }
        
        // Show appropriate message
        if (copied) {
          if (alreadyExists) {
            toast.success('📋 Link de acceso copiado (código existente)');
          } else {
            toast.success('📋 Link de acceso generado y copiado');
          }
        } else {
          // Show URL in modal if copy failed
          toast.success(
            <div className="space-y-2">
              <p className="font-semibold">
                {alreadyExists ? 'Link de acceso existente:' : 'Link de acceso generado:'}
              </p>
              <p className="text-sm bg-slate-700 p-2 rounded break-all">{portalUrl}</p>
              <p className="text-xs text-slate-300">Copia manualmente este link</p>
            </div>,
            { duration: 5000 }
          );
        }
        
        // Refresh playlists to show new code
        refetchPlaylists();
      }
    } catch (error) {
      console.error('Error generating access code:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al generar código de acceso: ${errorMsg}`);
    }
  };

  const openPlaylistDetail = async (playlist) => {
    // Push URL FIRST → crea entrada en el historial del navegador (base del
    // "botón atrás" funcional) Y evita la regresión del hook `useUrlEventSync`
    // que resetea state cuando ve URL vacía + currentPlaylist activo.
    // Sin este navigate(), tras el setCurrentPlaylist el efecto veía la URL
    // aún en "/" y hacía UNDO del click.
    if (playlist?.id) {
      navigate(`/?view=playlist-detail&playlistId=${playlist.id}`);
    }
    try {
      // Recargar datos del evento desde el backend para asegurar que están actualizados
      const response = await axios.get(`${API}/playlists/${playlist.id}`);
      const updatedPlaylist = response.data;
      
      setCurrentPlaylist(updatedPlaylist);
      fetchPlaylistSongs(updatedPlaylist.id);

      // Nota: la hidratación de `customListPlayedState` se hace via useEffect
      // que reacciona a `currentPlaylist.custom_list_played_state`. Ver más abajo.

      // If playlist has custom list, load the songs
      if (updatedPlaylist.lista_personalizada_generada && updatedPlaylist.lista_personalizada_generada.length > 0) {
        try {
          const songsResponse = await axios.post(`${API}/songs/by-ids`, {
            ids: updatedPlaylist.lista_personalizada_generada
          });
          
          setGeneratedCustomList(songsResponse.data.songs);
          setFilteredCustomList(songsResponse.data.songs);
          logger.log('[Playlist Load] Loaded custom list:', songsResponse.data.songs.length, 'songs');
        } catch (error) {
          console.error('Error loading custom list songs:', error);
        }
      } else {
        setGeneratedCustomList([]);
        setFilteredCustomList([]);
      }
      
      setView("playlist-detail");
      
      // Refetch playlists para sincronizar con el backend
      await refetchPlaylists();
    } catch (error) {
      console.error('Error loading playlist details:', error);
      // Si falla, usar datos locales como fallback
      setCurrentPlaylist(playlist);
      fetchPlaylistSongs(playlist.id);
      setView("playlist-detail");
    }
  };

  // "Vista Hoy DJ": abrir evento + activar Modo En Vivo automáticamente.
  // Usado por el CTA "Abrir evento" de las tarjetas de hoy/próximos.
  const openEventInLiveMode = async (assignment) => {
    await openPlaylistDetail(assignment);
    setIsLiveMode(true);
    toast.success('🔴 Modo En Vivo Activado');
  };

  const resetForm = () => {
    setFormData({
      title: "",
      artist: "",
      genres: [],
      bpm: "",
      rating: null,
      momentos: [],
      notas: "",
      favorito: false
    });
  };

  // clearAllFilters, availableGenres y hasActiveFilters vienen del contexto
  // Mantener getAllGenres para custom genres locales
  // getAllGenresLocal eliminado: código muerto (sin invocaciones en el codebase).

  // Calcular canciones paginadas (solo para vista biblioteca)
  const totalPages = Math.ceil(filteredSongs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSongs = view === "library" ? filteredSongs.slice(startIndex, endIndex) : filteredSongs;

  const renderSongCard = (song, showPlaylistActions = false) => (
    <Card key={song.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
      <CardContent className="p-2 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          {/* Checkbox para selección múltiple (solo en biblioteca) */}
          {!showPlaylistActions && view === "library" && (
            <input
              type="checkbox"
              checked={selectedSongs.includes(song.id)}
              onChange={() => toggleSongSelection(song.id)}
              className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500 focus:ring-offset-slate-800 cursor-pointer"
            />
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="mb-1 sm:mb-2">
                  <h3 className="text-white font-semibold text-sm sm:text-base leading-tight mb-0.5 sm:mb-1">
                    {song.title}
                  </h3>
                  {song.artist && (
                    <p className="text-slate-400 text-xs sm:text-sm">{song.artist}</p>
                  )}
                </div>

                <div className="flex flex-wrap gap-1 sm:gap-2 mb-1 sm:mb-2">
              {song.momentos && song.momentos.slice(0, 2).map((momento) => (
                <span key={momento} className="inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 bg-purple-600/30 text-purple-300 rounded text-xs">
                  {momento}
                </span>
              ))}
              {song.genres && song.genres.slice(0, 2).map((genre) => (
                <span key={genre} className="inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 bg-cyan-600/30 text-cyan-300 rounded text-xs">
                  {genre}
                </span>
              ))}
              {song.bpm && (
                <span className="inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 bg-slate-700 text-slate-300 rounded text-xs">
                  {song.bpm} BPM
                </span>
              )}
              {song.energia && (
                <span className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs ${
                  song.energia === 'Muy Alta' ? 'bg-red-600/30 text-red-300' :
                  song.energia === 'Alta' ? 'bg-orange-600/30 text-orange-300' :
                  song.energia === 'Media' ? 'bg-yellow-600/30 text-yellow-300' :
                  'bg-blue-600/30 text-blue-300'
                }`}>
                  {song.energia === 'Muy Alta' ? '⚡⚡⚡⚡' :
                   song.energia === 'Alta' ? '⚡⚡⚡' :
                   song.energia === 'Media' ? '⚡⚡' : '⚡'} {song.energia}
                </span>
              )}
                </div>

                {song.rating > 0 ? (
                  <div className="mb-1 sm:mb-2 scale-75 sm:scale-100 origin-left">
                    <StarRating rating={song.rating} editable={true} onRate={(newRating) => handleDirectRatingChange(song, newRating)} />
                  </div>
                ) : (
                  <div className="mb-1 sm:mb-2 scale-75 sm:scale-100 origin-left">
                    <StarRating rating={0} editable={true} onRate={(newRating) => handleDirectRatingChange(song, newRating)} />
                  </div>
                )}

                {song.notas && (
                  <p className="text-slate-400 text-xs italic line-clamp-1 sm:line-clamp-2 hidden sm:block">
                    {song.notas}
                  </p>
                )}
              </div>

              <div className="flex flex-row gap-1 sm:gap-2 flex-wrap sm:flex-nowrap">
            {!showPlaylistActions ? (
              <>
                {song.spotify_url && song.spotify_url.trim() !== '' ? (
                  <>
                    <button
                      onClick={() => {
                        // Abrir en app externa de Spotify (sin nueva pestaña en móvil)
                        window.location.href = song.spotify_url;
                      }}
                      className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors bg-green-600/20 touch-manipulation"
                      title="Abrir en Spotify"
                    >
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    </button>
                    <button
                      onClick={() => handleSearchSpotify(song.id)}
                      className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors bg-orange-600/20 touch-manipulation"
                      title="🔄 Corregir enlace de Spotify"
                    >
                      <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleSearchSpotify(song.id)}
                    className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors bg-green-600/20 touch-manipulation"
                    title="🔍 Buscar en Spotify"
                  >
                    <LinkIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                  </button>
                )}
                <button
                  onClick={() => toggleFavorite(song)}
                  className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors touch-manipulation"
                  title={song.favorito ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                >
                  <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${song.favorito ? 'fill-pink-500 text-pink-500' : 'text-slate-400'}`} />
                </button>
                {currentPlaylist && currentPlaylist.id && (
                  <button
                    onClick={(e) => {
                      handleAddSongToCustomList(song.id);
                    }}
                    className="p-1.5 sm:p-2 rounded bg-purple-600/20 hover:bg-purple-600/40 transition-colors touch-manipulation"
                    title="Añadir a lista personalizada del evento actual"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                  </button>
                )}
                <button
                  onClick={() => openEditDialog(song)}
                  className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors touch-manipulation"
                  title="Editar canción"
                >
                  <Edit2 className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(song.id);
                  }}
                  className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors touch-manipulation"
                  title="Eliminar canción"
                >
                  <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => openEditDialog(song)}
                  className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors touch-manipulation"
                  title="Editar canción"
                >
                  <Edit2 className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                </button>
                <button
                  onClick={() => handleRemoveSongFromPlaylist(song.id)}
                  className="p-1.5 sm:p-2 rounded hover:bg-slate-700 transition-colors touch-manipulation"
                  title="Quitar de playlist"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                </button>
              </>
            )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Handle global search navigation
  const handleGlobalSearchNavigate = (type, item) => {
    switch (type) {
      case 'evento':
        openPlaylistDetail(item);
        break;
      case 'cancion':
        setView('library');
        // Could highlight the song in the library
        break;
      case 'tarea':
        setEditingTask(item);
        setIsTaskFormOpen(true);
        break;
      default:
        break;
    }
  };

  // ===========================================================================
  // Contextos descompuestos (Fase 3 — Feb 2026)
  // ---------------------------------------------------------------------------
  // Mantenidos como inline value={{...}}: un useMemo aquí no aportaría memoiza-
  // ción real porque los handlers se recrean en cada render y harían las deps
  // mutar siempre. La separación por dominio sigue siendo el beneficio principal.
  // ===========================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 safe-top safe-x">
      {/* Edge-Swipe Back (iOS PWA): arrastrar desde borde izquierdo → back */}
      <EdgeSwipeBackIndicator />

      {/* PWA Offline Indicator */}
      <OfflineIndicator />
      
      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* Auto-Update Notification */}
      <UpdateNotification />
      
      <div className="container mx-auto px-2 sm:px-3 py-2 sm:py-4 max-w-7xl">
        {/* Header */}
        <div className="mb-3 sm:mb-4">
          {/* Logo and title row */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <img src="/logo.png" alt="Luis Herrera Eventos" className="h-12 sm:h-20 md:h-24 w-auto flex-shrink-0" />
              <h1 className="text-sm sm:text-xl md:text-2xl font-bold text-white truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <span className="hidden md:inline">Luis Herrera Eventos DJ TEAM</span>
                <span className="hidden sm:inline md:hidden">Luis Herrera</span>
                <span className="sm:hidden">LH Eventos</span>
              </h1>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-slate-300 text-sm hidden lg:block">
                {user?.username}
              </span>
              {/* Badge "Hoy" — acceso rápido al evento del día */}
              <TodayBadge />
              {/* Sync Status Badge */}
              <SyncStatusBadge />
              <Button
                onClick={logout}
                variant="outline"
                size="sm"
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              >
                <LogOut className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Salir</span>
              </Button>
            </div>
          </div>
          
          {/* Admin buttons row - separate row on mobile */}
          {user?.role === 'admin' && !isLiveMode && (
            <AdminNavButtons
              view={view}
              setView={setView}
              navigate={navigate}
              fetchPrecios={fetchPrecios}
              setIsDossierOpen={setIsDossierOpen}
              fetchTemplates={fetchTemplates}
              fetchSmartPlaylistTemplates={fetchSmartPlaylistTemplates}
            />
          )}
        </div>

        {/* Navigation Tabs — extraído a components/AppTabs.jsx (Ola A refactor) */}
        <AppTabs
          view={view}
          setView={setView}
          user={user}
          isLiveMode={isLiveMode}
          playlists={playlists}
          currentPlaylist={currentPlaylist}
          setCurrentPlaylist={setCurrentPlaylist}
          navigate={navigate}
          setIsTaskFormOpen={setIsTaskFormOpen}
        />

        {/* Dashboard View - Solo Admin */}
        {view === "dashboard" && user?.role === 'admin' && (
          <div className="space-y-6">
            <Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}>
            <Dashboard 
              playlists={playlists} 
              songs={songs}
              totalSongs={totalSongs}
              tasks={tasks}
              user={user} 
              denonPendientesCount={denonPendientesCount}
              onSync={async () => {
                logger.log('[Sync] Starting sync process...');
                try {
                  // First sync pending changes
                  logger.log('[Sync] Syncing pending changes...');
                  const success = await syncPendingChanges(axios);
                  logger.log('[Sync] Pending changes synced:', success);
                  
                  // Then download fresh data and save to cache
                  logger.log('[Sync] Downloading fresh data...');
                  toast.info('📥 Descargando datos para cache offline...');
                  
                  // Fetch and save songs
                  logger.log('[Sync] Fetching songs...');
                  const songsResponse = await axios.get(`${API}/songs`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  logger.log('[Sync] Songs response:', songsResponse.data?.songs?.length || songsResponse.data?.data?.length || 0);
                  const songsToSave = songsResponse.data?.songs || songsResponse.data?.data || [];
                  if (songsToSave.length > 0) {
                    await saveSongs(songsToSave);
                    logger.log('[Sync] Songs saved');
                    toast.success(`📚 ${songsToSave.length} canciones guardadas`);
                  } else {
                    logger.log('[Sync] No songs to save');
                  }
                  
                  // Fetch and save playlists
                  logger.log('[Sync] Fetching playlists...');
                  const playlistsResponse = await axios.get(`${API}/playlists`);
                  logger.log('[Sync] Playlists response:', playlistsResponse.data?.length || 0);
                  if (playlistsResponse.data && playlistsResponse.data.length > 0) {
                    await savePlaylists(playlistsResponse.data);
                    logger.log('[Sync] Playlists saved');
                    toast.success(`📅 ${playlistsResponse.data.length} eventos guardados`);
                  }
                  
                  // Update stats
                  logger.log('[Sync] Updating cache stats...');
                  updateCacheStats();
                  
                  logger.log('[Sync] Sync complete!');
                  toast.success('✅ Sincronización completa');
                  
                  return true;
                } catch (error) {
                  console.error('[Sync] Error:', error);
                  toast.error('Error al sincronizar: ' + error.message);
                  return false;
                }
              }}
              onClearCache={async () => {
                if (confirm('¿Estás seguro de limpiar el cache offline? Tendrás que volver a cargar todos los datos.')) {
                  await clearCache();
                  toast.success('🗑️ Cache limpiado');
                }
              }}
              onNavigateToEvent={async (evento) => {
                try {
                  await openPlaylistDetail(evento);
                } catch (error) {
                  console.error('Error loading event details:', error);
                  toast.error('Error al cargar el evento');
                }
              }}
            />
            </Suspense>
          </div>
        )}

        {/* Redirigir a biblioteca si no es admin */}
        {view === "dashboard" && user?.role !== 'admin' && (
          <div className="space-y-6">
            {/* Vista Hoy DJ — eventos del día actual (mobile-first) */}
            <TodayDJView
              assignments={myAssignments}
              onOpenEvent={openEventInLiveMode}
            />

            {/* Spotify Controls arriba - Acceso rápido */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SpotifyControls />
              <SpotifyQueue />
            </div>
            
            {/* Header con filtros */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">Mis Asignaciones</h2>
                <p className="text-slate-400">Eventos donde participas como DJ o Asistente</p>
              </div>
              
              <div className="flex flex-wrap gap-3 items-center">
                {/* Toggle ocultar pasados */}
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hidePastAssignments}
                    onChange={(e) => {
                      setHidePastAssignments(e.target.checked);
                      loadMyAssignments(assignmentFilterEstado, e.target.checked);
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                  />
                  Ocultar pasados
                </label>
                
                {/* Filtro por estado */}
                <select
                  value={assignmentFilterEstado}
                  onChange={(e) => {
                    setAssignmentFilterEstado(e.target.value);
                    loadMyAssignments(e.target.value, hidePastAssignments);
                  }}
                  className="bg-slate-800 text-white border-slate-700 rounded-lg px-4 py-2"
                >
                  <option value="">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="confirmado">Confirmado</option>
                  <option value="completado">Completado</option>
                </select>
                
                <Button
                  onClick={() => loadMyAssignments(assignmentFilterEstado, hidePastAssignments, { notify: true })}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  disabled={loadingAssignments}
                >
                  {loadingAssignments ? '⏳ Cargando...' : '🔄 Actualizar'}
                </Button>
              </div>
              
              {/* Última actualización */}
              {lastAssignmentsUpdate && (
                <p className="text-xs text-slate-500 mt-2">
                  Última actualización: {lastAssignmentsUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  <span className="text-slate-600">Se actualiza automáticamente cada minuto</span>
                </p>
              )}
            </div>

            {/* Lista de asignaciones */}
            {loadingAssignments ? (
              <div className="text-center py-12">
                <p className="text-slate-400">Cargando asignaciones...</p>
              </div>
            ) : myAssignments.length === 0 ? (
              hiddenPastCount > 0 ? (
                <div className="text-center py-12" data-testid="assignments-empty-hidden-past">
                  <p className="text-slate-400 mb-4">
                    No tienes eventos próximos. Tienes {hiddenPastCount} {hiddenPastCount === 1 ? 'evento pasado oculto' : 'eventos pasados ocultos'}.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white"
                    data-testid="assignments-show-past-btn"
                    onClick={() => {
                      setHidePastAssignments(false);
                      loadMyAssignments(assignmentFilterEstado, false);
                    }}
                  >
                    Mostrar eventos pasados
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12" data-testid="assignments-empty">
                  <p className="text-slate-400 mb-4">No tienes asignaciones aún.</p>
                  <p className="text-slate-500 text-sm">Contacta con tu administrador para que te asigne eventos.</p>
                </div>
              )
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {myAssignments.map((assignment) => (
                  <Card 
                    key={assignment.id} 
                    className="bg-slate-800 border-slate-700 hover:bg-slate-750 transition-colors cursor-pointer"
                    onClick={() => openPlaylistDetail(assignment)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{assignment.nombre_novios}</h3>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              assignment.estado === 'completado' ? 'bg-green-900/30 text-green-400' :
                              assignment.estado === 'confirmado' ? 'bg-blue-900/30 text-blue-400' :
                              'bg-orange-900/30 text-orange-400'
                            }`}>
                              {assignment.estado}
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-900/30 text-purple-400">
                              {assignment.my_role}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-3">
                      {/* Fecha y hora */}
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-slate-300">
                          <span className="text-blue-400 mr-2">📅</span>
                          <span>{assignment.fecha_evento || 'Sin fecha'}</span>
                        </div>
                        
                        {assignment.horario_inicio && (
                          <div className="flex items-center text-sm text-slate-300">
                            <span className="text-blue-400 mr-2">🕐</span>
                            <span>{assignment.horario_inicio} - {assignment.horario_fin || '...'}</span>
                          </div>
                        )}
                        
                        {assignment.lugar_evento && (
                          <div className="flex items-center text-sm text-slate-300">
                            <span className="text-blue-400 mr-2">📍</span>
                            <span>{assignment.lugar_evento}</span>
                          </div>
                        )}
                      </div>

                      {/* Información del evento */}
                      <div className="pt-2 border-t border-slate-700 space-y-1 text-sm text-slate-400">
                        {assignment.duracion_baile && (
                          <div className="flex justify-between">
                            <span>Duración baile:</span>
                            <span className="text-white">{assignment.duracion_baile}</span>
                          </div>
                        )}
                        
                        {assignment.numero_invitados && (
                          <div className="flex justify-between">
                            <span>Invitados:</span>
                            <span className="text-white">{assignment.numero_invitados}</span>
                          </div>
                        )}
                        
                        {assignment.custom_list_songs > 0 && (
                          <div className="flex justify-between">
                            <span>Lista personalizada:</span>
                            <span className="text-cyan-400">{assignment.custom_list_songs} canciones</span>
                          </div>
                        )}
                      </div>

                      {/* Equipo */}
                      {(assignment.nombre_dj || assignment.nombre_asistente) && (
                        <div className="pt-2 border-t border-slate-700 text-sm">
                          {assignment.nombre_dj && (
                            <div className="flex items-center gap-2 text-slate-300">
                              <span className="text-purple-400">🎧</span>
                              <span>DJ: {assignment.nombre_dj}</span>
                            </div>
                          )}
                          {assignment.nombre_asistente && (
                            <div className="flex items-center gap-2 text-slate-300">
                              <span className="text-purple-400">👤</span>
                              <span>Asistente: {assignment.nombre_asistente}</span>
                            </div>
                          )}
                          {assignment.nombre_asistente2 && (
                            <div className="flex items-center gap-2 text-slate-300">
                              <span className="text-purple-400">👤</span>
                              <span>Asistente 2: {assignment.nombre_asistente2}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Contacto */}
                      {(assignment.telefono_novios || assignment.email_novios) && (
                        <div className="pt-2 border-t border-slate-700 flex gap-2">
                          {assignment.telefono_novios && (
                            <a
                              href={`https://wa.me/34${assignment.telefono_novios.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 text-center text-xs px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                              📱 WhatsApp
                            </a>
                          )}
                          {assignment.email_novios && (
                            <a
                              href={`mailto:${assignment.email_novios}`}
                              className="flex-1 text-center text-xs px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                            >
                              ✉️ Email
                            </a>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {/* PWA Install Banner al final */}
            <div className="mt-6">
              <InstallPWA isPortal={false} />
            </div>
          </div>
        )}

        {/* Library View */}
        {view === "library" && (
          <SongLibrary
            user={user}
            isLiveMode={isLiveMode}
            denonPendientesCount={denonPendientesCount}
            fetchDenonPendientesCount={fetchDenonPendientesCount}
            customGenres={customGenres}
            setCustomGenres={setCustomGenres}
            getAllMomentos={getAllMomentos}
            MOMENTOS_BODA={MOMENTOS_BODA}
            onAddSong={() => setIsAddDialogOpen(true)}
            onEditSong={openEditDialog}
            onDeleteSong={handleDelete}
            onToggleFavorite={handleToggleFavorite}
            onToggleDenon={handleToggleDenon}
            onDirectRatingChange={handleDirectRatingChange}
            selectedSongs={selectedSongs}
            setSelectedSongs={setSelectedSongs}
            handleMultipleSelection={handleMultipleSelection}
            onOpenSpotifySearch={() => setIsSpotifySearchDialogOpen(true)}
            onDetectDuplicates={detectDuplicates}
            onAddStyle={() => setIsAddStyleDialogOpen(true)}
            onDeleteStyle={() => setIsDeleteStyleDialogOpen(true)}
            onAddMomento={handleAddMomento}
            onDeleteMomento={handleDeleteMomento}
            onAutoAssignStyle={handleAutoAssignStyle}
            onBulkAutoAssignStyle={handleBulkAutoAssignStyle}
            onLinkAllSpotify={handleLinkAllSpotify}
            onRelinkSongToSpotify={handleRelinkSongToSpotify}
            onOpenSmartPlaylists={() => setShowSmartPlaylistsDialog(true)}
            onOpenPlaylistAnalyzer={() => setIsPlaylistAnalyzerOpen(true)}
          />
        )}

        {/* Playlists View */}
        {view === "playlists" && (
          <EventsList
            onSelectPlaylist={openPlaylistDetail}
            onEditPlaylist={openEditPlaylist}
            onDeletePlaylist={handleDeletePlaylist}
            onGenerateAccessCode={generateClientAccessCode}
            isCreatePlaylistOpen={isCreatePlaylistOpen}
            setIsCreatePlaylistOpen={setIsCreatePlaylistOpen}
            handleCreatePlaylist={handleCreatePlaylist}
            handleDeletePastEvents={handleDeletePastEvents}
            handleDeleteAllEvents={handleDeleteAllEvents}
            user={user}
          />
        )}

        {/* Playlist Detail View */}
        {view === "playlist-detail" && currentPlaylist && (
          <AudioPlaybackContext.Provider value={{
            playingAudioId, setPlayingAudioId,
            audioStates, setAudioStates,
            audioProgress, setAudioProgress,
            preloadAudio, cachedAudios,
          }}>
          <PeticionesContext.Provider value={{
            peticionNota, setPeticionNota,
            peticionesPendingFlags, setPeticionesPendingFlags,
            peticionesSearchQuery, setPeticionesSearchQuery,
            peticionesSearchResults, setPeticionesSearchResults,
            peticionesSearching, setPeticionesSearching,
            isPeticionesDialogOpen, setIsPeticionesDialogOpen,
            handlePeticionesDragEnd, handleRemovePeticion, handleTogglePeticionHidden,
          }}>
          <CustomListContext.Provider value={{
            customListPlayedState,
            generatedCustomList, setGeneratedCustomList,
            filteredCustomList, setFilteredCustomList,
            listaPersonalizadaFilterEnergia, setListaPersonalizadaFilterEnergia,
            listaPersonalizadaFilterGenre, setListaPersonalizadaFilterGenre,
            listaPersonalizadaFilterMomento, setListaPersonalizadaFilterMomento,
            listaPersonalizadaSearch, setListaPersonalizadaSearch,
            listaPersonalizadaViewMode, setListaPersonalizadaViewMode,
            handleRemoveSongFromCustomList,
            handleReorderCustomList,
            handleReorderMomentosGlobal,
            handleResetCustomListPlayed,
            handleToggleCustomListPlayed,
            handleExportCustomListPDF,
          }}>
          <NotesContext.Provider value={{
            sharedNotes,
            setCurrentNote, setEditingNoteId,
            handleEditNote, handleDeleteNote,
            setIsNotesDialogOpen,
          }}>
          <MainAppContext.Provider value={{
            accessToken,
            buscarEnBiblioteca,
            buscarEnSpotify,
            cacheStats,
            clearEventCache,
            createSong,
            currentPlaylist,
            currentTab,
            downloadAllEventAudios,
            downloadProgress,
            downloadingAll,
            enrichSongWithMarkers,
            fetchDenonPendientesCount,
            fetchPlaylistTemplates,
            fetchSmartPlaylistTemplates,
            getAllGenres,
            getAllMomentos,
            getCachedData,
            groupSongsByMomentos,
            handleAddSongToPlaylist,
            handleDeletePlaylist,
            handleExportToEngineDJ,
            handleExportToSpotify,
            handleGenerateAIRecommendationsForEvent,
            handleLongPressEditSong,
            handleTabChange,
            isAudioUploaderOpen,
            isGeneratingRecommendations,
            isLiveMode,
            loading,
            location,
            openEditPlaylist,
            playlistSongs,
            playlists,
            queueOfflineAction,
            refetchPlaylists,
            setBuscarEnBiblioteca,
            setBuscarEnSpotify,
            setCachedData,
            setCurrentPlaylist,
            setIsAddToPlaylistOpen,
            setIsApplyTemplateOpen,
            setIsAudioUploaderOpen,
            setIsLiveMode,
            setIsMixChainsOpen,
            setIsMixSuggestionsOpen,
            setIsSaveTemplateOpen,
            setMixChainsMode,
            setMixSuggestionsSong,
            setSearchGenre,
            setSearchText,
            setSelectedSongsForChain,
            setShowClientTextImporter,
            setShowEventWizard,
            setShowGenreAnalysis,
            setShowWhatsAppImporter,
            setSpotifySearchCache,
            setTemplateForm,
            setView,
            showGenreAnalysis,
            songs,
            spotifyConnected,
            spotifySearchCache,
            token,
            user,
            view
          }}>
            <ErrorBoundary
              fallbackTitle="Error en la vista del evento"
              onReset={() => {
                setView("playlists");
                setCurrentPlaylist(null);
                if (location.search.includes('view=playlist-detail')) {
                  navigate('/', { replace: true });
                }
              }}
            >
              <PlaylistDetailView />
            </ErrorBoundary>
          </MainAppContext.Provider>
          </NotesContext.Provider>
          </CustomListContext.Provider>
          </PeticionesContext.Provider>
          </AudioPlaybackContext.Provider>
        )}

        {/* Event Wizard Modal */}
        {showEventWizard && currentPlaylist && (
          <EventWizard
            eventoId={currentPlaylist.id}
            onClose={() => setShowEventWizard(false)}
            onPlaylistSaved={async () => {
              // Refresh the current playlist data AND the songs list
              try {
                const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
                setCurrentPlaylist(response.data);
                // Also refresh the playlist songs to update the "Lista generada" view
                await fetchPlaylistSongs(currentPlaylist.id);
              } catch (e) {
                console.error('Error refreshing playlist:', e);
              }
              setShowEventWizard(false);
            }}
          />
        )}

        {/* WhatsApp Chat Importer */}
        <WhatsAppImporter
          isOpen={showWhatsAppImporter}
          onClose={() => setShowWhatsAppImporter(false)}
          eventId={currentPlaylist?.id}
          eventName={currentPlaylist?.nombre_novios}
          onAnalysisComplete={async (analysis) => {
            // Refresh playlist data after WhatsApp review was applied
            // (el toast "Aplicado: ..." ya lo muestra ClientTextImportDialog)
            try {
              const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
              setCurrentPlaylist(response.data);
            } catch (e) {
              console.error('Error refreshing playlist:', e);
            }
          }}
        />

        {/* Client Text/Email Importer - iter92.B */}
        <ClientTextImportDialog
          open={showClientTextImporter}
          onClose={() => setShowClientTextImporter(false)}
          eventId={currentPlaylist?.id}
          eventName={currentPlaylist?.nombre_novios}
          onApplied={async () => {
            // Refrescar evento tras aplicar extracción
            try {
              const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
              setCurrentPlaylist(response.data);
            } catch (e) {
              console.error('Error refreshing playlist:', e);
            }
          }}
        />

        {/* Mix Suggestions Modal */}
        <MixSuggestionsModal
          isOpen={isMixSuggestionsOpen}
          onClose={() => {
            setIsMixSuggestionsOpen(false);
            setMixSuggestionsSong(null);
          }}
          song={mixSuggestionsSong}
          token={token}
          onInsertAfter={async (songIdToInsert, librarySongId) => {
            // Insertar la canción justo después de la canción base
            if (currentPlaylist && mixSuggestionsSong) {
              const baseSongId = mixSuggestionsSong.id;
              const songId = librarySongId || songIdToInsert;
              try {
                await handleInsertSongAfter(songId, baseSongId);
              } catch (e) {
                console.error('Error inserting song:', e);
              }
            }
          }}
          onAddToLibrary={async (suggestion) => {
            // Add a new song to the library from AI suggestion
            try {
              const response = await axios.post(
                `${API}/dj-assistant/add-song`,
                {
                  title: suggestion.title,
                  artist: suggestion.artist,
                  spotify_url: suggestion.spotify_url,
                  genre: suggestion.genre,
                  bpm: suggestion.bpm
                },
                { headers: { Authorization: `Bearer ${token}` }}
              );
              
              if (response.data.success) {
                toast.success(response.data.message);
                refetchSongs();
                // Si la canción se añadió a biblioteca, también insertarla en la lista
                if (response.data.song_id && mixSuggestionsSong) {
                  await handleInsertSongAfter(response.data.song_id, mixSuggestionsSong.id);
                }
              } else {
                toast.info(response.data.message);
              }
            } catch (error) {
              console.error('Error adding song to library:', error);
              toast.error('Error al añadir canción a la biblioteca');
            }
          }}
        />

        {/* Mix Chains Manager Modal */}
        <MixChainsManager
          isOpen={isMixChainsOpen}
          onClose={() => {
            setIsMixChainsOpen(false);
            setSelectedSongsForChain([]);
          }}
          token={token}
          playlistId={currentPlaylist?.id}
          currentPlaylistSongs={generatedCustomList}
          onChainApplied={async () => {
            // Refrescar la lista de canciones después de aplicar una cadena
            if (currentPlaylist?.id) {
              await fetchPlaylistSongs(currentPlaylist.id);
            }
          }}
          selectedSongs={selectedSongsForChain}
          mode={mixChainsMode}
        />

        {/* Users View - MOVED TO /users route */}
        {/* Precios View - MOVED TO /precios route */}
        {/* Team Dashboard View - MOVED TO /team route */}
        {/* Financial Management View - MOVED TO /finances route */}
        {/* Presupuestos Independientes View - MOVED TO /presupuestos route */}
        {/* Plantillas View - MOVED TO /plantillas route */}

        {/* Calendar View */}
        {view === "calendar" && (
          <Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}>
          <CalendarView 
            onSelectEvent={handleCalendarEventSelect}
            onSelectTask={(task) => {
              setEditingTask(task);
              setIsTaskFormOpen(true);
            }}
            onCreateEvent={user?.role === 'admin' ? (date) => {
              setCurrentPlaylist(null);
              // Si la llamada viene de clic en una celda del calendario,
              // date es un Date object → convertir a string YYYY-MM-DD para el form
              if (date instanceof Date && !isNaN(date.getTime())) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                setCreatePlaylistInitialDate(`${y}-${m}-${d}`);
              } else {
                setCreatePlaylistInitialDate(null);
              }
              setIsCreatePlaylistOpen(true);
            } : undefined}
            onDeleteEvent={user?.role === 'admin' ? handleDeletePlaylist : undefined}
          />
          </Suspense>
        )}

        {/* Tasks View - Solo Admin */}
        {view === "tasks" && user?.role === 'admin' && (
          <TasksView
            tasks={tasks}
            onEditTask={(task) => {
              setSeriesEditMode(null); // Reset mode for normal edit
              setEditingTask(task);
              setIsTaskFormOpen(true);
            }}
            onCompleteTask={completeTask}
            onDeleteTask={deleteTask}
            onDeleteTaskSeries={deleteTaskSeries}
            onEditTaskSeries={editTaskSeries}
          />
        )}

        {/* Reports View - Solo Admin */}
        {view === "reports" && user?.role === 'admin' && (
          <InformesView 
            playlists={playlists}
            onSelectEvent={async (playlist) => {
              setLoadedTabs(new Set(['lista']));
              setCurrentTab('lista');
              try {
                await openPlaylistDetail(playlist);
              } catch (error) {
                console.error('[ERROR] Failed to load playlist:', error);
                setCurrentPlaylist(playlist);
                setView("playlist-detail");
                toast.warning('Usando datos cacheados del evento');
              }
            }}
          />
        )}

        {/* Tendencias View */}
        {view === "tendencias" && (
          <div className="space-y-6">
            <TendenciasView />
            
            {/* Live Trends - Nueva sección */}
            <LiveTrendsView 
              eventoId={currentPlaylist?.id}
              onAddToPlaylist={(song) => {
                // Callback opcional para añadir a playlist
                logger.log('Add to playlist:', song);
              }}
            />
          </div>
        )}

        {/* Dialog para Añadir Canción a Lista Personalizada — extraído a components/AddSongToPlaylistDialog.jsx (Ola A) */}
        <AddSongToPlaylistDialog
          isOpen={isAddToPlaylistOpen}
          onClose={() => setIsAddToPlaylistOpen(false)}
          songs={songs}
          currentPlaylist={currentPlaylist}
          availableGenres={availableGenres}
          searchText={searchText}
          setSearchText={setSearchText}
          searchGenre={searchGenre}
          setSearchGenre={setSearchGenre}
          addDialogFilterRating={addDialogFilterRating}
          setAddDialogFilterRating={setAddDialogFilterRating}
          addDialogShowFavoritesOnly={addDialogShowFavoritesOnly}
          setAddDialogShowFavoritesOnly={setAddDialogShowFavoritesOnly}
          onAddSong={handleAddSongToCustomList}
        />

        {/* Edit Dialog — extraído a components/EditDialog.jsx (Ola C) */}
        <EditDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleEdit}
          getAllGenres={getAllGenres}
          getAllMomentos={getAllMomentos}
        />

        {/* Edit Playlist Dialog */}
        <Dialog open={isEditPlaylistOpen} onOpenChange={setIsEditPlaylistOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">Editar Playlist / Evento</DialogTitle>
            </DialogHeader>
            {editingPlaylist && (
              <PlaylistForm
                initialData={editingPlaylist}
                onSubmit={handleEditPlaylist}
                onCancel={() => { setIsEditPlaylistOpen(false); setEditingPlaylist(null); }}
                submitLabel="Actualizar Playlist"
                isAdmin={user?.role === 'admin'}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Contract Generation Dialog removed (Ola D cleanup): UI trigger was deleted in Dec 2025 commit e7da893 — see GestionContratos.jsx for the active contract flow. */}

        {/* Precio dialogs removed (Ola E): wiring moved inside PreciosManager.jsx — see components/EditPrecioDialog.jsx + components/NewPrecioDialog.jsx. */}

        {/* Dialog para Guardar como Plantilla */}
        <Dialog open={isSaveTemplateOpen} onOpenChange={setIsSaveTemplateOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">Guardar Lista como Plantilla</DialogTitle>
              <p className="text-slate-400 text-sm">
                Guarda esta lista personalizada como plantilla para reutilizarla en otros eventos
              </p>
            </DialogHeader>
            <form onSubmit={handleSaveAsTemplate} className="space-y-4">
              <div>
                <Label htmlFor="template-nombre">Nombre de la Plantilla *</Label>
                <Input
                  id="template-nombre"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={templateForm.nombre}
                  onChange={(e) => setTemplateForm({...templateForm, nombre: e.target.value})}
                  placeholder="Ej: Boda Clásica, Fiesta Latina"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="template-descripcion">Descripción</Label>
                <Textarea
                  id="template-descripcion"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={templateForm.descripcion}
                  onChange={(e) => setTemplateForm({...templateForm, descripcion: e.target.value})}
                  placeholder="Describe esta plantilla..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="template-tipo">Tipo de Evento</Label>
                <Input
                  id="template-tipo"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={templateForm.tipo_evento}
                  onChange={(e) => setTemplateForm({...templateForm, tipo_evento: e.target.value})}
                  placeholder="Ej: Boda, Cumpleaños, Corporativo"
                />
              </div>
              
              <div>
                <Label htmlFor="template-tags">Etiquetas (separadas por comas)</Label>
                <Input
                  id="template-tags"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={templateForm.tags}
                  onChange={(e) => setTemplateForm({...templateForm, tags: e.target.value})}
                  placeholder="Ej: Pop, Romántico, Energético"
                />
              </div>
              
              <div className="bg-slate-900/50 p-3 rounded">
                <p className="text-sm text-slate-300">
                  <strong>Canciones en la lista:</strong> {currentPlaylist?.lista_personalizada_generada?.length || 0}
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  onClick={() => setIsSaveTemplateOpen(false)}
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Guardar Plantilla
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog para Aplicar Plantilla */}
        <Dialog open={isApplyTemplateOpen} onOpenChange={setIsApplyTemplateOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">Aplicar Plantilla</DialogTitle>
              <p className="text-slate-400 text-sm">
                Selecciona una plantilla para aplicar al evento actual
              </p>
            </DialogHeader>
            
            {/* Plantillas de Eventos */}
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Plantillas de Eventos
                </h3>
                <div className="space-y-3">
                  {playlistTemplates.length === 0 ? (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      <p>No hay plantillas de eventos guardadas</p>
                    </div>
                  ) : (
                    playlistTemplates.map(template => (
                  <div
                    key={template.id}
                    className="p-4 rounded-lg bg-slate-900/50 hover:bg-slate-900 border border-slate-700"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-white font-semibold text-lg">{template.nombre}</h4>
                        {template.descripcion && (
                          <p className="text-slate-400 text-sm mt-1">{template.descripcion}</p>
                        )}
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {template.tipo_evento && (
                            <span className="px-2 py-1 bg-blue-600/30 text-blue-300 rounded text-xs">
                              {template.tipo_evento}
                            </span>
                          )}
                          {template.tags && template.tags.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-cyan-600/30 text-cyan-300 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-4 mt-2 text-xs text-slate-400">
                          <span>🎵 {template.song_ids?.length || 0} canciones</span>
                          {template.times_used > 0 && <span>✨ Usado {template.times_used} {template.times_used === 1 ? 'vez' : 'veces'}</span>}
                          {template.duracion_estimada && <span>⏱️ {template.duracion_estimada}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApplyTemplate(template.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aplicar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (!template.song_ids || template.song_ids.length === 0) {
                              toast.warning('Esta plantilla no tiene canciones guardadas. Aplícala primero a un evento para generar la lista.');
                              return;
                            }
                            handleExportTemplateToEngineDJ(template.id, template.nombre);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                          title="Exportar a Engine DJ (M3U8)"
                          disabled={!template.song_ids || template.song_ids.length === 0}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          🎧 M3U8
                        </Button>
                        {(user?.role === 'admin' || template.created_by === user?.email) && (
                          <Button
                            size="sm"
                            onClick={() => handleDeletePlaylistTemplate(template.id)}
                            variant="outline"
                            className="border-red-600 text-red-400 hover:bg-red-600/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                    ))
                  )}
                </div>
              </div>

              {/* Smart Playlists */}
              <div>
                <h3 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  Plantillas de Playlist Inteligente
                </h3>
                <div className="space-y-3">
                  {smartPlaylistTemplates.length === 0 ? (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      <p>No hay plantillas inteligentes guardadas</p>
                    </div>
                  ) : (
                    smartPlaylistTemplates.map(template => (
                      <div
                        key={template.id}
                        className="p-4 rounded-lg bg-slate-900/50 hover:bg-slate-900 border border-slate-700"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-white font-semibold text-lg">{template.name}</h4>
                            <div className="flex gap-4 mt-2 text-xs text-slate-400">
                              <span>🎵 {template.songs?.length || 0} canciones</span>
                              {template.criteria?.genres && template.criteria.genres.length > 0 && (
                                <span>🎸 {template.criteria.genres.slice(0, 2).join(', ')}{template.criteria.genres.length > 2 ? '...' : ''}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleApplySmartPlaylist(template)}
                            className="bg-cyan-600 hover:bg-cyan-700"
                            disabled={!template.songs || template.songs.length === 0}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Aplicar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button
                onClick={() => setIsApplyTemplateOpen(false)}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700"
              >
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicates Dialog */}
        <Dialog open={isDuplicatesDialogOpen} onOpenChange={setIsDuplicatesDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">Canciones Duplicadas Detectadas</DialogTitle>
            </DialogHeader>
            
            {duplicateGroups.length > 0 ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-700/50 rounded-lg">
                  <Button
                    onClick={detectDuplicates}
                    disabled={isDetectingDuplicates}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    size="sm"
                  >
                    {isDetectingDuplicates ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        🔍 Buscar Similares
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={detectExactDuplicates}
                    disabled={isDetectingDuplicates}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-sm"
                    size="sm"
                  >
                    {isDetectingDuplicates ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        ✓ Buscar Duplicados Exactos
                      </>
                    )}
                  </Button>
                  <div className="text-xs text-slate-400 flex items-center ml-2">
                    <span className="bg-blue-900/30 px-2 py-1 rounded mr-2">🔍 Similares: algoritmo fuzzy</span>
                    <span className="bg-purple-900/30 px-2 py-1 rounded">✓ Exactos: título + artista idénticos</span>
                  </div>
                </div>
                <p className="text-slate-300">
                  Se encontraron {duplicateGroups.reduce((total, group) => total + group.songs.length, 0)} canciones duplicadas en {duplicateGroups.length} grupos.
                  Para cada grupo, se mantendrá la mejor canción (marcada en verde) y se eliminarán las demás.
                </p>
                
                <div className="space-y-6">
                  {duplicateGroups.map((group, groupIndex) => {
                    // Get the selected song for this group from the map
                    const selectedToKeep = selectedToKeepMap[groupIndex] || group.best_song_id;
                    // Count how many will be deleted in this group
                    const toDeleteCount = group.songs.filter(item => item.song.id !== selectedToKeep).length;
                    // Find the best rated song in the group
                    const bestRatedSong = group.songs.reduce((best, current) => {
                      const currentRating = current.song.rating || 0;
                      const bestRating = best?.song?.rating || 0;
                      return currentRating > bestRating ? current : best;
                    }, group.songs[0]);
                    
                    return (
                      <div key={groupIndex} className="border border-slate-600 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              Grupo {groupIndex + 1} - {group.songs.length} canciones {group.match_type === 'exact' ? 'idénticas' : 'similares'}
                            </h3>
                            <p className="text-xs text-slate-400 mt-1">
                              Se eliminará{toDeleteCount !== 1 ? 'n' : ''} <span className="text-red-400 font-medium">{toDeleteCount}</span> canción{toDeleteCount !== 1 ? 'es' : ''}, 
                              se mantendrá <span className="text-green-400 font-medium">1</span>
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // Remove this group from duplicateGroups
                              const newGroups = duplicateGroups.filter((_, idx) => idx !== groupIndex);
                              setDuplicateGroups(newGroups);
                              
                              // Also update the map
                              const newMap = {...selectedToKeepMap};
                              delete newMap[groupIndex];
                              setSelectedToKeepMap(newMap);
                              
                              toast.info('Grupo ignorado');
                            }}
                            className="text-xs text-slate-400 hover:text-white"
                          >
                            ✕ Ignorar Grupo
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {group.songs.map((item, songIndex) => {
                            const song = item.song;
                            const isSelected = song.id === selectedToKeep;
                            const isBestRated = song.id === bestRatedSong?.song?.id && (song.rating || 0) > 0;
                            
                            return (
                              <div 
                                key={song.id}
                                className={`p-3 rounded-lg border ${
                                  isSelected 
                                    ? 'bg-green-900/30 border-green-600/50' 
                                    : 'bg-slate-800/50 border-slate-600/50'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {/* Radio button to select which to keep */}
                                  <div className="pt-1">
                                    <input
                                      type="radio"
                                      name={`group-${groupIndex}`}
                                      checked={isSelected}
                                      onChange={() => {
                                        // Update the map with the new selection
                                        setSelectedToKeepMap(prev => ({
                                          ...prev,
                                          [groupIndex]: song.id
                                        }));
                                        // Update the group's best_song_id
                                        group.best_song_id = song.id;
                                      }}
                                      className="w-4 h-4 cursor-pointer accent-green-500"
                                    />
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        isSelected 
                                          ? 'bg-green-600 text-white' 
                                          : 'bg-red-900/60 text-red-300'
                                      }`}>
                                        {isSelected ? '✓ MANTENER' : '✕ ELIMINAR'}
                                      </span>
                                      {isBestRated && (
                                        <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-600/80 text-yellow-100">
                                          ⭐ Más valorada
                                        </span>
                                      )}
                                      <span className="text-white font-medium">{song.title}</span>
                                      <span className="text-slate-400">por {song.artist}</span>
                                    </div>
                                    
                                    <div className="mt-2 text-sm text-slate-300">
                                      <div className="flex flex-wrap gap-4">
                                        <span className={song.rating ? 'text-yellow-400 font-medium' : 'text-slate-500'}>
                                          {song.rating ? `${'⭐'.repeat(song.rating)}` : 'Sin rating'}
                                        </span>
                                        <span>BPM: {song.bpm || 'N/A'}</span>
                                        <span>Energía: {song.energia || 'N/A'}</span>
                                        <span>Favorito: {song.favorito ? '❤️' : '🤍'}</span>
                                        {song.spotify_url && <span className="text-green-400">🎵 Spotify</span>}
                                      </div>
                                      {song.genres && song.genres.length > 0 && (
                                        <div className="mt-1">
                                          Géneros: {song.genres.join(', ')}
                                        </div>
                                      )}
                                      {song.momentos && song.momentos.length > 0 && (
                                        <div className="mt-1">
                                          Momentos: {song.momentos.join(', ')}
                                        </div>
                                      )}
                                      <div className="mt-1 text-xs text-slate-400">
                                        Score interno: {item.score} puntos | ID: {song.id.substring(0, 8)}...
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Individual delete button */}
                                  <button
                                    onClick={() => handleDeleteDuplicate(song.id, groupIndex)}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded"
                                    title="Eliminar solo esta canción"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-slate-600 flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              // Delete all songs in this group except the selected one
                              const songsToDelete = group.songs
                                .filter(item => item.song.id !== selectedToKeep)
                                .map(item => item.song.id);
                              
                              if (songsToDelete.length === 0) {
                                toast.info('No hay canciones para eliminar en este grupo');
                                return;
                              }
                              
                              if (window.confirm(`¿Eliminar ${songsToDelete.length} canción${songsToDelete.length !== 1 ? 'es' : ''} de este grupo?`)) {
                                songsToDelete.forEach(id => handleDeleteDuplicate(id, groupIndex));
                              }
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs"
                          >
                            🗑️ Limpiar Grupo (mantener seleccionada)
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex flex-col gap-4 pt-4 border-t border-slate-600">
                  {/* Info box */}
                  <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 text-lg">💡</span>
                      <div className="text-sm text-blue-200">
                        <p className="font-medium mb-1">Selección automática inteligente:</p>
                        <p className="text-blue-300/80">La canción con más ⭐ estrellas (y más información) está pre-seleccionada automáticamente en verde. Puedes cambiar la selección manualmente si lo prefieres.</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <Button
                      onClick={() => setIsDuplicatesDialogOpen(false)}
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      Cerrar
                    </Button>
                    <Button
                      onClick={handleDeleteAllDuplicates}
                      className="bg-red-600 hover:bg-red-700 text-white font-medium"
                      data-testid="delete-all-duplicates-btn"
                    >
                      🗑️ BORRAR TODAS LAS DUPLICADAS ({duplicateGroups.reduce((total, group, idx) => {
                        const selectedToKeep = selectedToKeepMap[idx] || group.best_song_id;
                        return total + group.songs.filter(item => item.song.id !== selectedToKeep).length;
                      }, 0)} canciones)
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-300">No se encontraron duplicados</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Notes Dialog — extraído a components/NotesDialog.jsx (Ola D) */}
        <NotesDialog
          isOpen={isNotesDialogOpen}
          onOpenChange={setIsNotesDialogOpen}
          currentNote={currentNote}
          setCurrentNote={setCurrentNote}
          editingNoteId={editingNoteId}
          setEditingNoteId={setEditingNoteId}
          onCreate={handleCreateNote}
          onUpdate={handleUpdateNote}
        />

        {/* Add Song Dialog */}
        {/* Añadir Nueva Canción — extraído a components/AddSongDialog.jsx (Ola B) */}
        <AddSongDialog
          isOpen={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          getAllGenres={getAllGenres}
          getAllMomentos={getAllMomentos}
        />

        {/* Spotify Search Dialog */}
        {isSpotifySearchDialogOpen && (
          <SpotifySearchDialog
            isOpen={isSpotifySearchDialogOpen}
            setIsOpen={setIsSpotifySearchDialogOpen}
            onAddSong={(songData) => {
              setFormData({ ...formData, ...songData });
              setIsAddDialogOpen(true);
            }}
          />
        )}

        {/* Add Style Dialog — extraído a components/AddStyleDialog.jsx (Ola D) */}
        <AddStyleDialog
          isOpen={isAddStyleDialogOpen}
          onOpenChange={setIsAddStyleDialogOpen}
          newStyleName={newStyleName}
          setNewStyleName={setNewStyleName}
          onAdd={handleAddStyle}
        />

        {/* Delete Style Dialog — extraído a components/DeleteStyleDialog.jsx (Ola D) */}
        <DeleteStyleDialog
          isOpen={isDeleteStyleDialogOpen}
          onOpenChange={setIsDeleteStyleDialogOpen}
          selectedGenreToDelete={selectedGenreToDelete}
          setSelectedGenreToDelete={setSelectedGenreToDelete}
          genres={getAllGenres()}
          onDelete={handleDeleteGenre}
        />

        {/* Botón flotante para salir del Modo En Vivo */}
        {isLiveMode && (view === 'dashboard' || view === 'library' || view === 'tendencias') && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              onClick={() => {
                setIsLiveMode(false);
                setView('playlist-detail');
                toast.success('✓ Volviendo al evento');
              }}
              size="lg"
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-2xl animate-pulse px-8 py-6 text-lg font-bold"
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Salir del Modo En Vivo
            </Button>
          </div>
        )}

        {/* Smart Playlists Dialog */}
        {showSmartPlaylistsDialog && (
          <SmartPlaylists
            genres={getAllGenres()}
            onClose={() => setShowSmartPlaylistsDialog(false)}
            onApplyToCustomList={(songs) => {
              // Aplicar las canciones generadas a la lista personalizada del evento actual
              if (currentPlaylist && currentPlaylist.id) {
                const songIds = songs.map(s => s.id);
                setGeneratedCustomList(songs);
                
                // Actualizar el playlist en el backend
                axios.put(`${API}/playlists/${currentPlaylist.id}`, {
                  lista_personalizada_generada: songIds
                }).then(() => {
                  toast.success('Playlist inteligente aplicada a la lista personalizada');
                }).catch(err => {
                  console.error('Error updating playlist:', err);
                  toast.error('Error al aplicar playlist');
                });
              } else {
                toast.error('Selecciona un evento primero');
              }
            }}
          />
        )}

        {/* Playlist Analyzer Dialog */}
        {isPlaylistAnalyzerOpen && (
          <PlaylistAnalyzer
            onClose={() => setIsPlaylistAnalyzerOpen(false)}
            onSongsUpdated={() => {
              // Invalidate all song queries to force fresh data
              queryClient.invalidateQueries({ queryKey: ['songs'] });
              refetchSongs();
            }}
          />
        )}

        {/* Spotify Send to Queue Dialog */}
        {isSpotifySendDialogOpen && (
          <Dialog open={isSpotifySendDialogOpen} onOpenChange={setIsSpotifySendDialogOpen}>
            <DialogContent className="w-[95vw] bg-slate-900 text-white border-slate-700 max-w-md">
              <DialogHeader>
                <DialogTitle>🎵 Enviar a Cola de Spotify</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-sm text-slate-300">
                  ¿Enviar {selectedSongs.length} {selectedSongs.length === 1 ? 'canción' : 'canciones'} a la cola de Spotify?
                </p>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="spotify-shuffle"
                      checked={spotifySendOptions.shuffle}
                      onChange={(e) => setSpotifySendOptions({...spotifySendOptions, shuffle: e.target.checked})}
                      className="rounded"
                    />
                    <label htmlFor="spotify-shuffle" className="text-sm">
                      🔀 Activar modo aleatorio
                    </label>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">🔁 Modo de repetición:</label>
                    <select
                      value={spotifySendOptions.repeat}
                      onChange={(e) => setSpotifySendOptions({...spotifySendOptions, repeat: e.target.value})}
                      className="w-full bg-slate-800 border-slate-700 text-white rounded px-3 py-2"
                    >
                      <option value="off">Desactivado</option>
                      <option value="context">Repetir playlist</option>
                      <option value="track">Repetir canción</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsSpotifySendDialogOpen(false)}
                  className="bg-slate-800 border-slate-700 hover:bg-slate-700"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={sendToSpotify}
                  className="bg-green-600 hover:bg-green-700"
                >
                  ✅ Enviar a Cola
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Task Form Dialog */}
        {/* Global Create Event Dialog - Accessible from Calendar and other views */}
        <Dialog open={isCreatePlaylistOpen} onOpenChange={(open) => {
          setIsCreatePlaylistOpen(open);
          // Limpiar fecha pre-rellenada al cerrar el dialog
          if (!open) setCreatePlaylistInitialDate(null);
        }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">
                Crear Nuevo Evento
                {createPlaylistInitialDate && (
                  <span className="ml-2 text-sm font-normal text-emerald-300">
                    · {new Date(createPlaylistInitialDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <PlaylistForm
              initialData={createPlaylistInitialDate ? {
                nombre_novios: '',
                fecha_evento: createPlaylistInitialDate,
                lugar_evento: '',
                horario_inicio: '',
                horario_fin: '',
                tipo_evento: '',
                gustos_musicales: '',
                canciones_prohibidas: '',
                canciones_imperdibles: '',
              } : undefined}
              onSubmit={handleCreatePlaylist}
              onCancel={() => {
                setIsCreatePlaylistOpen(false);
                setCreatePlaylistInitialDate(null);
              }}
            />
          </DialogContent>
        </Dialog>

        <TaskForm
          isOpen={isTaskFormOpen}
          onClose={() => {
            setIsTaskFormOpen(false);
            setEditingTask(null);
            setSeriesEditMode(null);
          }}
          onSubmit={async (taskData) => {
            if (editingTask) {
              // Si hay un modo de edición de serie, usar updateTaskSeries
              if (seriesEditMode && (editingTask.is_recurring || editingTask.parent_task_id)) {
                await updateTaskSeries(editingTask.id, taskData, seriesEditMode);
              } else {
                await updateTask(editingTask.id, taskData);
              }
            } else {
              await createTask(taskData);
            }
          }}
          onDelete={deleteTask}
          initialData={editingTask}
          seriesEditMode={seriesEditMode}
        />

        {/* Keyboard Shortcuts Help Modal */}
        <KeyboardShortcutsHelp
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
        />

        {/* Global Search (Cmd+K) */}
        <GlobalSearch
          open={isGlobalSearchOpen}
          onOpenChange={setIsGlobalSearchOpen}
          onNavigate={handleGlobalSearchNavigate}
        />

        {/* Dossier Generator Modal */}
        <Dialog open={isDossierOpen} onOpenChange={setIsDossierOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-transparent border-none">
            <DossierGenerator onClose={() => setIsDossierOpen(false)} />
          </DialogContent>
        </Dialog>

        {/* DJ Assistant - Botón flotante */}
        <DJAssistant
          eventContext={currentPlaylist ? {
            playlist_id: currentPlaylist.id,
            tipo_evento: currentPlaylist.tipo_evento,
            fecha: currentPlaylist.fecha_evento,
            lugar: currentPlaylist.lugar_evento,
            edad_media: currentPlaylist.edad_media_invitados,
            gustos_musicales: currentPlaylist.gustos_musicales,
            canciones_prohibidas: currentPlaylist.canciones_prohibidas,
            canciones_imprescindibles: currentPlaylist.canciones_imprescindibles,
            hora_loca_cancion: currentPlaylist.hora_loca_cancion,
            nombre_evento: currentPlaylist.nombre_novios || currentPlaylist.nombre_evento
          } : null}
          onPlaylistUpdated={async () => {
            // Tras aplicar cambios desde la pestaña "Texto" (ej. nuevas peticiones
            // con momento), refresca el evento abierto para que se vean sin recargar.
            if (!currentPlaylist?.id) return;
            try {
              const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
              setCurrentPlaylist(response.data);
            } catch (e) {
              console.error('Error refrescando evento tras aplicar Texto:', e);
            }
          }}
        />

      </div>
    </div>
  );
}

export default AuthenticatedApp;
